import { createApi } from '@reduxjs/toolkit/query/react';

import { quickBooksApiQuery } from './base-query';
import type { FiservBatchDetail } from './fiserv-batch-detail';
import { controlCenterNeedsAttentionSearch } from './needs-attention-request';
import {
  controlCenterEventWorkspaceSearch,
  controlCenterEventWorkspaceUnavailableError,
} from './event-workspace-request';
import {
  readRestInBackground,
  firstPageWhileReading,
  CONTROL_CENTER_SERVER_MAX_LIMIT,
} from './control-center-list-pages';

export type KillSwitchEngageRequest = { reason: string };
export type KillSwitchReleaseRequest = {
  reason: string;
  acknowledgement: 'I_ACKNOWLEDGE_QBO_WRITE_RELEASE';
};

export { controlCenterEventWorkspaceSearch } from './event-workspace-request';

export type QuickBooksEnvironment = 'sandbox' | 'production';

export interface QuickBooksConnection {
  id: string;
  realmId: string;
  lastRefreshedAt?: string;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  environment: QuickBooksEnvironment;
  status: 'error' | 'active' | 'disconnected' | 'refresh_required';
}

export interface QuickBooksMapping {
  id?: string;
  sourceKey: string;
  qboItemId?: string;
  qboAccountId: string;
  qboAccountName: string;
  mappingType:
    | 'fee'
    | 'tax'
    | 'tender'
    | 'payout'
    | 'refund'
    | 'shipping'
    | 'chargeback'
    | 'sale_income'
    | 'review_residual'
    // Owner decisions round 2, 2026-08-19 -- must stay in lockstep with the
    // backend DB enum, model, and mappingsBody request schema. Omitting it
    // here hides the residual destination from the Control Center, leaving
    // the split-residual path permanently inert.
    | 'expense_category'
    | 'accrued_liability';
}

export interface QuickBooksAccount {
  id: string;
  name: string;
  active?: boolean;
  accountType?: string;
  accountSubType?: string;
  classification?: string;
  fullyQualifiedName?: string;
}

export interface QuickBooksSettings {
  id: string;
  enabled: boolean;
  killSwitch: boolean;
  closedThroughDate?: string | null;
  scheduleJson: Record<string, unknown>;
  cashControlsAudit?: {
    changedAt: string;
    actorId: number | null;
    actorName: string | null;
  } | null;
}

// "What was it?" (qb-worker quickbooksPayoutResidualLink.js
// payoutResidualLinkView): a new CLIX payout item recorded as the extra the
// bank paid on a payout (the same-day residual held in CLIX Review).
export interface QuickBooksPayoutResidualLink {
  id: number;
  eventId: number;
  payoutId: number;
  employeeId: number;
  reviewItemId: number;
  undoneAt: string | null;
  createdBy: number | null;
  createdAt: string | null;
  appliedAt: string | null;
  categoryId: number | null;
  residualPurchaseId: string;
  linkedPayoutItemId: number;
  residualAmountCents: number;
  linkedPayoutId: number | null;
  linkedTypeLabel: string | null;
  status: 'active' | 'undone' | 'applied';
  linkedType: 'Misc' | 'Bonus' | 'Advance' | 'Reimbursements';
  // Where the worker re-books the line (Needs attention only).
  target?: {
    status: string;
    category: string | null;
    accountName: string | null;
  };
}

// A link as a payout list row carries it (qb-worker
// quickbooksPayoutResidualLink.js readPayoutResidualLinksByPayout): only
// in-force links (active, applied) are ever listed.
export interface QuickBooksPayoutResidualLinkOnPayout {
  id: number;
  payoutId: number;
  createdAt: string | null;
  appliedAt: string | null;
  residualPurchaseId: string;
  linkedPayoutItemId: number;
  role: 'LINKED' | 'ORIGINAL';
  residualAmountCents: number;
  status: 'active' | 'applied';
  linkedPayoutId: number | null;
  linkedTypeLabel: string | null;
  linkedType: 'Misc' | 'Bonus' | 'Advance' | 'Reimbursements';
}

// qb-worker quickbooksControlCenter.js settleResidualPayments.
export interface QuickBooksSettledBySameDayResidual {
  linkId: number | null;
  payoutId: number | null;
  appliedAt: string | null;
  qboEntityId: string | null;
  qboEntityType: string | null;
  linkStatus: 'active' | 'applied' | null;
}

// What the side panel needs to prefill CLIX's "Add row" popup, and whether a
// link can be recorded now (qb-worker quickbooksNeedsAttention.js overpay
// item `residualContext`). Absent fields are absent.
export interface QuickBooksOverpayResidualContext {
  txnDate?: string;
  payoutId?: number;
  canRecord: boolean;
  eventIds?: number[];
  reason: string | null;
  residualCents?: number;
  checkedAt?: string | null;
  employeeId?: number | null;
  reviewItemId: number | null;
  employeeName: string | null;
  paymentMethodId?: number | null;
}

export interface QuickBooksPayoutResidualLinkRequest {
  reviewItemId: number;
  linkedPayoutItemId: number;
}

export interface QuickBooksWorkerCircuitResetRequest {
  state: string;
  environment: string;
  resetRequestedAt: string;
  resetRequestedBy: string;
  reasonCode: string | null;
  evaluationSequence: string | number;
}

// GET /control-center/worker-status (clix-backend qb-worker
// quickbooksWorkerSchedulingStatus.js workerSchedulingStatusFromRow), read
// fresh from QbWorkerCircuit and QbSchedulerLeadership on every request.
export interface QuickBooksWorkerSchedulingStatus {
  asOf?: string;
  available: boolean;
  reasonCode?: string;
  environment: string | null;
  scheduler?: {
    owned: boolean;
    epoch: string | null;
    expiresAt: string | null;
    ownerBuild: string | null;
    unownedSince: string | null;
  };
  // The last circuit transitions, newest first (worker
  // quickbooksWorkerSchedulingStatus.js workerCircuitHistoryFromRows).
  circuitHistory?: {
    available: boolean;
    reasonCode?: string;
    transitions: Array<{
      id: string;
      at: string | null;
      closedBy: string | null;
      closedAt: string | null;
      openedAt: string | null;
      transition: string | null;
      reasonCode: string | null;
      tripReasonCode: string | null;
    }>;
  };
  circuit?: {
    state: string;
    open: boolean;
    openedAt: string | null;
    resetRequested: boolean;
    evaluationFresh: boolean;
    reasonCode: string | null;
    evaluatedAt: string | null;
    resetRequestedAt: string | null;
    // Automatic recovery (owner 14 Oct 2026), absent before its migration.
    autoRecovery?: {
      tripsIn24h: number;
      closedBy: string | null;
      closesAt: string | null;
      healthySince: string | null;
      tripReasonCode: string | null;
      recoveryWindowSeconds: number | null;
    } | null;
  } | null;
}

export interface QuickBooksProjectLink {
  id: string;
  realmId: string;
  eventId: number;
  qboProjectId: string;
  qboProjectName: string;
  state: 'blocked' | 'verified';
  environment: QuickBooksEnvironment;
  matchMethod: 'manual' | 'assisted';
  event?: { id: number; name: string; startDate?: string };
}

export interface QuickBooksProjectAutoLinkResult {
  projectsFound: number;
  candidatesFound: number;
  skipped: {
    reason: string;
    eventId?: number;
    qboProjectName?: string;
    qboProjectNames?: string[];
  }[];
  linked: {
    eventId: number;
    eventName?: string;
    qboProjectId: string;
    qboProjectName: string;
    expectedProjectName: string;
  }[];
}

export interface QuickBooksReviewItem {
  id: string;
  code: string;
  message: string;
  sourceType: string;
  sourceKey?: string | null;
  status: 'open' | 'resolved';
  severity: 'warning' | 'blocking';
  environment: QuickBooksEnvironment;
}

export interface QuickBooksDepositRunRequest {
  realmId: string;
  fiservImportId: number;
  environment: QuickBooksEnvironment;
}

export interface QuickBooksDepositRun {
  posted?: boolean;
  sourceKey: string;
  requestId: string;
  idempotent?: boolean;
  reviewRequired: boolean;
  alreadyPosted?: boolean;
  payload: Record<string, unknown>;
  review: {
    unmatchedRows: number;
    unsupportedRows: number;
  };
  readBack?: {
    id: string;
    txnDate?: string;
    lineCount: number;
  };
  summary: {
    total: number;
    feeTotal: number;
    feeCents: number;
    lineCount: number;
    totalCents: number;
    projectCount: number;
  };
}

export interface QuickBooksFiservImportRequest {
  pageSize?: number;
  reportDate: string;
  batchNumber: string;
  currentPageIndex?: number;
  rawRows?: FiservBatchDetail[];
}

export interface QuickBooksFiservImportProjectSummary {
  count: number;
  amount: number;
  eventId: number;
  amountCents: number;
  eventName?: string | null;
}

export interface QuickBooksFiservMatchResult {
  id: number;
  amountCents: number;
  fiservRowKey: string;
  eventId?: number | null;
  refundId?: number | null;
  eventName?: string | null;
  checkoutId?: number | null;
  chargebackId?: number | null;
  matchStrategy?: string | null;
  decision: 'matched' | 'unmatched' | 'ambiguous';
  matchType: 'refund' | 'unknown' | 'checkout' | 'chargeback' | 'adjustment';
}

export interface QuickBooksFiservImportResult {
  matches: QuickBooksFiservMatchResult[];
  import: {
    id: number;
    reportDate: string;
    batchNumber: string;
    importedAt?: string;
  };
  summary: {
    total: number;
    matched: number;
    unmatched: number;
    totalCents: number;
    matchedCount: number;
    matchedCents: number;
    unmatchedCount: number;
    unmatchedCents: number;
    byProject: QuickBooksFiservImportProjectSummary[];
  };
}

// H11, 2026-08-19 -- real request/response shape of
// GET /research/money-out/assignment-candidates
// (quickbooks.js#lookupPostedMoneyOutAssignmentCandidates). Unlike every
// other endpoint wired this session, this one makes a REAL live QBO
// Accounting API call on every request (#qboTransactionLookupRaw) -- there
// is no DB-only version of this lookup. UI must disclose that, not treat it
// as a normal local read.
export interface QuickBooksMoneyOutCandidatesQuery {
  dateTo: string;
  amount: string;
  realmId: string;
  dateFrom: string;
  merchant: string;
  accountId?: string;
  maxResults?: number;
  accountLast4?: string;
  direction?: 'spend' | 'refund';
  environment: QuickBooksEnvironment;
  entityType?: 'Check' | 'Purchase' | 'Transfer' | 'BillPayment';
}

export interface QuickBooksMoneyOutCandidate {
  id?: string;
  txnDate?: string;
  syncToken?: string;
  [key: string]: unknown;
  totalAmountCents?: number;
}

export interface QuickBooksMoneyOutCandidatesResult {
  source: string;
  realmId: string;
  boundary: string;
  researchOnly: true;
  updatePayloadWasSent: false;
  environment: QuickBooksEnvironment;
  queryResults: {
    count: number;
    error?: string;
    entityType: string;
    queryAccepted: boolean;
  }[];
  match: {
    dateMatches: number;
    amountMatches: number;
    accountMatches: number;
    directionMatches: number;
    deterministicCandidates: QuickBooksMoneyOutCandidate[];
    // The real, unmapped server verdict. Render verbatim -- especially
    // REVIEW_MULTIPLE_CANDIDATES, which must never be translated into UI
    // copy that reads like "pick one": nothing in the write path can
    // execute that choice. See event-workspace.tsx-adjacent H11 report.
    verdict: 'GO' | 'REVIEW_ZERO_CANDIDATES' | 'REVIEW_MULTIPLE_CANDIDATES';
  };
}

export interface QuickBooksMoneyOutRunRequest {
  realmId: string;
  sourceId: number;
  environment: QuickBooksEnvironment;
  sourceType: 'payout' | 'expense' | 'chargeback';
}

export interface QuickBooksMoneyOutRun {
  posted?: boolean;
  sourceKey: string;
  requestId: string;
  idempotent?: boolean;
  reviewRequired: boolean;
  alreadyPosted?: boolean;
  review: Record<string, unknown>;
  payload: Record<string, unknown>;
  entityMapSourceType: 'payout' | 'refund' | 'expense';
  readBack?: {
    id: string;
    txnDate?: string;
    lineCount: number;
  };
  summary: {
    total: number;
    sourceId: number;
    lineCount: number;
    totalCents: number;
    sourceType: 'payout' | 'expense' | 'chargeback';
  };
}

export interface QuickBooksControlCenterQuery {
  // GET /control-center/review-items only: one QbReviewItem code (qb-worker
  // quickbooksControlCenter.js reviewItems), echoed as filters.code.
  code?: string;
  // GET /control-center/history only: a comma list of activity kinds
  // (qb-worker quickbooksHistoryActivity.js HISTORY_KINDS).
  kind?: string;
  state?: string;
  limit?: number;
  search?: string;
  offset?: number;
  realmId?: string;
  sourceType?: string;
  itemsLimit?: number;
  itemsOffset?: number;
  // Event-workspace payload shaping. Keeping this in the RTK argument makes
  // the compact canonical projection a distinct cache entry from the legacy
  // full response shape.
  includeItems?: boolean;
  // Client-only tenant identity used in RTK Query's serialized cache key.
  // The API derives company ownership from the authenticated session, so this
  // value is deliberately removed before URL construction.
  cacheCompanyId?: number;
  linkedSourceId?: string;
  eventId?: number | string;
  environment?: QuickBooksEnvironment;
  linkedSourceType?: QuickBooksLtlSourceType;
}

export interface QuickBooksQboEntity {
  id?: string | null;
  accountRef?: unknown;
  // Instructor ruling, 2026-08-20 -- real fields qboEntityFromPosted()
  // (quickbooksControlCenterEnrichment.js) always returns, previously
  // fetched and discarded by the frontend. status is QbEntityMap's own
  // enum ('posted' | 'superseded' | 'error'); lastPushedAt is when CLIX
  // last recorded pushing this entity. Used by the evidence drawer's
  // qbo-card to state plainly whether an item has been re-checked since
  // it was recorded, rather than a generic disclaimer.
  status?: string | null;
  // Present (always null) on an entity built from a verified worker effect
  // (applyVerifiedWorkerEffects, quickbooksVerifiedWorkerEffects.js).
  amount?: number | null;
  txnDate?: string | null;
  syncToken?: string | null;
  docNumber?: string | null;
  entityType?: string | null;
  lastPushedAt?: string | null;
}

// One QBO write the worker made and proved (same-ID read-back + no-write
// replay): publicVerifiedEffect(), quickbooksVerifiedWorkerEffects.js. A
// claim-backed write has claimId; a source-origin JournalEntry has
// entityMapId instead (claimId null).
export interface QuickBooksVerifiedOwnerReview {
  id: number;
  code: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type QuickBooksVerifiedWriteLane =
  | 'PAYOUT'
  | 'DEPOSIT'
  | 'EXPENSE'
  | 'SOURCE_ORIGIN';

export interface QuickBooksVerifiedEffect {
  ownerId: string;
  ownerType: string;
  sourceKey: string;
  qboEntityId: string;
  replayStatus: string;
  qboEntityType: string;
  claimId: number | null;
  syncToken: string | null;
  mutationOccurred: boolean;
  verifiedAt: string | null;
  entityMapId: number | null;
  replacementCreated: boolean;
  verificationAuditId: number;
  kind: 'CLAIM' | 'SOURCE_ORIGIN';
  sameIdReadBackVerified: boolean;
  sourceFingerprint: string | null;
  lane: string | QuickBooksVerifiedWriteLane;
  proofStatus: 'WRITE_VERIFIED' | 'ALREADY_CORRECT';
  // Every open review on the claimed owner, stale or not; the row stays in
  // Review only for those raised after verifiedAt (the server's own rule).
  openOwnerReviews: QuickBooksVerifiedOwnerReview[];
}

// quickbooksControlCenter.js staleEventRecheckFor. `at` is when the queued
// check was captured (QUEUED), the next daily check (SCHEDULED), or when the
// check ran or failed (CHECKED, FAILED).
export interface QuickBooksStaleEventRecheck {
  at: string | null;
  reasonCode: string | null;
  queuedSince?: string | null;
  // BACKLOGGED (pkg6 V8): the queued re-check has waited longer than the
  // worker's bound; queuedSince is when it was queued. Older workers send
  // neither.
  state:
    | 'QUEUED'
    | 'FAILED'
    | 'CHECKED'
    | 'SCHEDULED'
    | 'BACKLOGGED'
    | 'NOT_SCHEDULED';
}

// pkg6 V9: an event Dev already wrote, held from Production until the owner
// decides (qb-worker handoverPendingEventIds). Absent on other events and on
// older workers.
export interface QuickBooksHeldForOwner {
  held: boolean;
  what?: string | null;
  reasonCode?: string | null;
}

// pkg6 V1: per-category totals over the whole filtered expense set, not the
// page (qb-worker /control-center/expenses). Payout reimbursements are not
// in it.
export interface QuickBooksExpenseCategoryTotal {
  label: string;
  count: number;
  cents: number;
  categoryKey: string;
  parentCount?: number;
  unknownAmountCount?: number;
}

// pkg6 V6/V7: whether Fiserv settlements were ever imported for the
// company. NOT_SET_UP: none, so card deposits cannot be matched.
export interface QuickBooksFiservImportState {
  importCount?: number | null;
  lastImportedAt?: string | null;
  settlementCount?: number | null;
  state: 'UNKNOWN' | 'IMPORTED' | 'NOT_SET_UP';
}

export type QuickBooksSourceLookupStatus =
  | 'NOT_FOUND'
  | 'NOT_CHECKED'
  | 'UNAVAILABLE'
  | 'SEVERAL_FIT'
  | 'DATE_DIFFERS'
  | 'OTHER_ACCOUNT'
  | 'ON_CLIX_REVIEW'
  | 'BOOKED_AS_CLIX'
  | 'FILED_DIFFERENTLY'
  | 'FOUND_NOT_COMPARED'
  | 'BANK_NOT_POSTED_YET';

// qb-worker quickbooksSourceLookupStatus.js classifySourceLookup.
export interface QuickBooksSourceLookup {
  reason?: string | null;
  lagDays?: number | null;
  fitCount?: number | null;
  sourceKey?: string | null;
  checkedAt?: string | null;
  expectedBy?: string | null;
  status: string | QuickBooksSourceLookupStatus;
  counts?: Record<string, number | null> | null;
  search?: {
    dateTo?: string | null;
    dateFrom?: string | null;
    accountId?: string | null;
    amountCents?: number | null;
    accountLabel?: string | null;
  } | null;
  // FILED_DIFFERENTLY: what CLIX says the entry must hold.
  expected?: {
    accountId?: string | null;
    totalCents?: number | null;
    accountName?: string | null;
    projects?: { projectId?: string | null; amountCents?: number | null }[];
  } | null;
  entity?: {
    id?: string | null;
    txnDate?: string | null;
    docNumber?: string | null;
    entityType?: string | null;
    amountCents?: number | null;
    lines?: QuickBooksEntryLine[];
  } | null;
  // SEVERAL_FIT: the entries that fit, as the worker summarized them.
  candidates?: {
    id?: string | null;
    payee?: string | null;
    txnDate?: string | null;
    docNumber?: string | null;
    entityType?: string | null;
    amountCents?: number | null;
    accountName?: string | null;
  }[];
}

export interface QuickBooksControlCenterItem {
  state: string;
  itemKey: string;
  sourceType: string;
  code?: string | null;
  // Review records only (quickbooksControlCenterSource.js
  // reviewItemToLegacyReviewRow): plain words for `code` from
  // quickbooksReviewCodeLabels.js; null when that table has none.
  label?: string | null;
  payee?: string | null;
  // PIXEL-MATCH-SPEC.md §11.4/§11.5 -- reviewTypes is the real trigger for a
  // REVIEW-state payment (the PayoutItem.type(s) classifyItem() always
  // routes to review); `reviewReasons` above is reused for a
  // FAILED_HARD/FAILED_RETRYABLE event-load-failure placeholder's real
  // error message; amountMismatchCents is null unless the real
  // Payout.total genuinely disagrees with the sum of this payment's own
  // allocations.
  reviewTypes?: string[];
  // Car Rentals T2 (round-13, 2026-08-20) -- vendor/invoiceNumber/
  // confirmationNumber/renterName/rentalDate/dueDate come from a real
  // reverse-FK join (QbRentalImportRow.rentalActualId), not a column on
  // QbRentalActual itself -- honest null when no import row matched.
  // eventAllocations is the real per-event percent split
  // (Expense.Events, ExpenseEvent.percent) -- can be empty (no expenseId
  // resolved) or carry more than one entry (a rental split across
  // events). amountCents is the canonical actual; openCents is 0 once
  // the rental reaches a terminal PAID/CREDITED/IGNORED state,
  // otherwise the same amountCents -- two real numbers, never one
  // printed twice. Only exists on a 'car_rental' row.
  vendor?: string | null;
  eventId?: number | null;
  message?: string | null;
  dueDate?: string | null;
  // Deposits grouping (round-10) -- null for a checkout-level group row,
  // which represents several real component rows at once and has no
  // single real sourceKey of its own (see quickbooksDepositGrouping.js).
  // Real and non-null on every ungrouped item (Refund/Chargeback, and
  // every other surface's rows).
  sourceKey: string | null;
  sourceId?: string | null;
  reviewReasons?: string[];
  // Payouts-surface-only fields (quickbooksPayoutGrouping.js's
  // groupPayoutItemsIntoPayments -- PIXEL-MATCH-SPEC.md §2/§8). One row per
  // real PAYMENT (grouped by payoutId), not per PayoutItem. `amount` above
  // is the real Payout.total; `eventSplit` is the separate per-event
  // allocation breakdown -- PLAN.md :149, two distinct numbers, never
  // collapsed. Absent on every other surface's items.
  payoutId?: number | null;
  category?: string | null;
  employeeNames?: string[];
  ageHours?: number | null;
  expenseId?: number | null;
  paymentMethods?: string[];
  openCents?: number | null;
  // LTL (round-13, 2026-08-20) -- real per-row fields joined server-side
  // (quickbooksControlCenter.js's ltl()), only exist on an 'ltl_shipment'
  // row. eventName is a flat sibling field here (this surface's own
  // controlCenterItem() envelope has no `event.eventName` the way
  // sourceRowToControlCenterItem's does for other families) -- honest
  // null when the shipment has no eventId, or the event does not
  // resolve. carrierName comes from a read-only Carriers lookup (see
  // that join's own comment for why the read is safe). actualCents is
  // the shipment's most-recent real invoice total; claimingBatch* mirror
  // Car Rentals' claimingBatchKey pattern, one level deeper (shipment ->
  // invoice -> active batch claim -> batch).
  eventName?: string | null;
  bolNumber?: string | null;
  payoutType?: string | null;
  // Legacy checkout fields remain component/drill-down provenance only.
  // The selected Deposits operator row is the server-owned money-in parent:
  // a provisional event/day credit bucket or confirmed full funding group,
  // never one accounting parent per checkout.
  checkoutId?: number | null;
  receiptUrl?: string | null;
  childCount?: number | null;
  employeeId?: number | null;
  renterName?: string | null;
  rentalDate?: string | null;
  // Car Rentals T3 (round-13, 2026-08-20) -- QbRentalBatch's own direct
  // columns, only exist on a 'car-rental-batches' row. invoiceCount is a
  // real QbRentalBatchClaim count (excluding released claims). Posted
  // amount/Difference are deliberately absent -- a table must never fire
  // a live QBO read per row.
  totalCents?: number | null;
  // pkg6 V2: Payout.date ('YYYY-MM-DD') on a Payouts row.
  payoutDate?: string | null;
  payoutFamilyKeys?: string[];
  // Expenses tab (round-11) -- description/paymentDate/paymentMethodId/
  // isRegisterCash only exist on an 'ExpenseEvent' row
  // (quickbooksSourceInventory.js's loadCompanyExpenseFamily); category also
  // carries the project-cost families' real label (COGS/LTL/Rent/
  // Electricity/COI/Shipping). employeeId/employeeName/paymentMethod are the
  // same fields Payouts already declares above, reused here. Undefined/null
  // on every other surface's items, harmless.
  description?: string | null;
  paymentDate?: string | null;
  amountCents?: number | null;
  carrierName?: string | null;
  actualCents?: number | null;
  payoutStatus?: string | null;
  allocationId?: number | null;
  employeeName?: string | null;
  accrualState?: string | null;
  invoiceCount?: number | null;
  freshnessAsOf?: string | null;
  paymentMethod?: string | null;
  invoiceNumber?: string | null;
  // Instructor ruling, 2026-08-20 -- an item with no real payoutId is not
  // a payment (groupPayoutItemsIntoPayments never merges it into a
  // fabricated shared payment), so it must never inflate a family chip's
  // tally. Its own honest predicate for the "Not attached to a payment"
  // chip, read directly from this backend fact.
  notAttachedToPayment?: boolean;
  candidateCount?: number | null;
  // A Payouts-list row standing for the payout's accounting movement (e.g.
  // itemKey payout:<id>:legacy-aggregate) names its payment row here.
  paymentItemKey?: string | null;
  // pkg6 V6: why a deposit row waits ('FISERV_NOT_IMPORTED').
  waitReasonCode?: string | null;
  // PIXEL-MATCH-SPEC.md §2's Overview attention table "Unresolved" column --
  // the same actionRequiredCount attentionChipFor() already used to build
  // `attention`'s "N items" wording, surfaced on the row. null (not 0) when
  // no inventory pipeline ran for this event (unlinked / failed to load),
  // same reason completionPercent is null in those cases.
  unresolvedCount?: number | null;
  payoutFamilyKey?: string | null;
  paymentMethodId?: number | null;
  isRegisterCash?: boolean | null;
  workOrderNumber?: string | null;
  claimingBatchId?: string | null;
  // Owner feedback + instructor ruling, 2026-08-20 -- which real
  // QbRentalBatch (by batchKey) currently holds this rental's one
  // allowed ACTIVE claim, joined server-side (claim() enforces at most
  // one active claim per rental, so this is never a list). Null is
  // honest "available for a batch," not "in batch —".
  claimingBatchKey?: string | null;
  // Named distinctly from the item envelope's own `sourceType`
  // ('ltl_shipment', this family's classification) -- the mockup's
  // "Shipment / source" origin (STOCK_ORDER/STOCK_TRANSFER/EVENT_EXPENSE).
  originSourceType?: string | null;
  // LTL payment batches (round-13, 2026-08-20) -- real QbLtlPaymentBatch
  // columns, only exist on an 'ltl_batch' row.
  paymentReference?: string | null;
  // Payout.total, carried on every PayoutItem and payout movement row
  // (quickbooksControlCenterSource.js controlCenterItem payoutTotalCents).
  payoutTotalCents?: number | null;
  completionPercent?: number | null;
  postedQboEntityId?: string | null;
  // pkg6 V4: a PayoutItem's amount with the Payout.total sign rule (an
  // advance is negative); `amount` stays the raw stored amount.
  signedAmountCents?: number | null;
  checkoutTotalCents?: number | null;
  confirmationNumber?: string | null;
  // Set only on an event row whose current source has moved on since the
  // event's last published revision (quickbooksControlCenter.js
  // withEventFreshness). freshnessAsOf is that revision's asOf.
  // STALE: behind its source; NOT_CHECKED: never checked by the worker yet.
  freshness?: 'STALE' | 'NOT_CHECKED';
  amountMismatchCents?: number | null;
  expenseSourceFamily?: string | null;
  postedQboEntityType?: string | null;
  qboEntities?: QuickBooksQboEntity[];
  confirmedTransactionDates?: string[];
  // Server-derived, scope-specific mapping facts for an ExpenseEvent child
  // or a category parent whose children agree. They distinguish the expense
  // category account from the payment/source account; neither is QBO proof.
  categoryMappingStatus?: string | null;
  reviewItemId?: string | number | null;
  // A Payouts row (qb-worker quickbooksPayoutGrouping.js
  // groupPayoutItemsIntoPayments and quickbooksControlCenter.js
  // projectPayoutPaymentRows): `items` are the payment's PayoutItem rows;
  // `accountingMovements` the PayoutTransaction / PayoutLegacyAggregate
  // movements that settle it (each with its own paymentDate, amount and, once
  // the worker verified it, qboEntities); `lifecycleMovements` the unpaid /
  // pending lifecycle rows of a payout no movement settles yet.
  items?: QuickBooksControlCenterItem[];
  // A verified worker write sets qboEntity only when there is exactly one
  // entity; qboEntities always lists them all (a payout paid in two
  // transfers has two Purchases and a null qboEntity).
  qboEntity?: QuickBooksQboEntity | null;
  componentMismatchCents?: number | null;
  // Review Queue (round-11) -- reviewItemToLegacyReviewRow's real shape
  // (quickbooksControlCenterSource.js), a QbReviewItem row reshaped onto
  // this same generic contract. Only exists on a review-items row;
  // undefined/null everywhere else, harmless.
  severity?: 'warning' | 'blocking' | null;
  accrualQboJournalEntryId?: string | null;
  // Status truth (2026-10-16): what the worker's newest read-only QuickBooks
  // lookup of this record found (qb-worker quickbooksSourceLookupStatus.js
  // withSourceLookups), on a row the worker has not booked
  // (WAITING_FOR_QBO_POST). Absent on older workers and other states.
  qboLookup?: QuickBooksSourceLookup | null;
  claimingBatchPaymentMethod?: string | null;
  paymentAccountMappingStatus?: string | null;
  verifiedEffects?: QuickBooksVerifiedEffect[];
  // pkg6 V9: on a Dev-held event row.
  heldForOwner?: QuickBooksHeldForOwner | null;
  claimingBatchPaymentReference?: string | null;
  expenseChildren?: QuickBooksControlCenterItem[];
  verifiedEffect?: QuickBooksVerifiedEffect | null;
  categorySet?: { name: string; id: number | null }[];
  accountingMovements?: QuickBooksControlCenterItem[];
  // What will re-check that stale event (quickbooksControlCenter.js
  // staleEventRecheckFor); null when the server could not read it.
  freshnessRecheck?: QuickBooksStaleEventRecheck | null;
  // The in-force links that involve this payout: ORIGINAL (its bank entry
  // paid the extra) or LINKED (its item explains the extra).
  payoutResidualLinks?: QuickBooksPayoutResidualLinkOnPayout[];
  amount?: {
    cents?: number;
    display?: string;
  } | null;
  categoryDetails?: import('store/api/event/types').CategoryDetails[];
  // "What was it?" on a Payouts / event payout row (qb-worker
  // quickbooksControlCenter.js settleResidualPayments and
  // attachPayoutResidualLinks). settledBySameDayResidual: this payout was
  // paid inside payout `payoutId`'s bank entry (state EXCLUDED, no bank
  // payment of its own); linkStatus null when the link was not readable.
  settledBySameDayResidual?: QuickBooksSettledBySameDayResidual | null;
  retry?: {
    retryCount?: number;
    nextRetryAt?: string | null;
  };
  // A funding parent shared by several events (fundingLineProjection /
  // mergeExactSharedMoneyInParents).
  events?: {
    eventId: number | null;
    eventName: string | null;
  }[];
  componentBreakdown?: { amountCents: number; sourceFamily: string | null }[];
  // Events-summary-row-only fields (quickbooksControlCenterSource.js's
  // mappedEventSummaryItems -- PLAN.md :472 / PIXEL-MATCH-SPEC.md §2/§3/§6).
  // Absent on every other surface's items.
  dates?: {
    endDate?: string | null;
    startDate?: string | null;
  } | null;
  attention?: {
    label: string;
    tone: 'ok' | 'bad' | 'warn' | 'mute';
  } | null;
  // A cross-event payout whose events all carry the same verified write but
  // project it differently (qb-worker quickbooksControlCenter.js
  // mergeExactPayoutTransactionParents): information, not a conflict.
  sharedProjectionDifference?: {
    eventIds: number[];
    reviewReasons: string[];
  } | null;
  // P6, 2026-08-20 -- a real payment can genuinely span multiple item
  // families (e.g. Earnings + Tips); `familyBreakdown` is every family
  // actually present, ordered by real dollar amount contributed (never
  // item count), and `payoutFamilyKeys` is the flat list of those keys for
  // multi-membership chip matching. `payoutFamilyKey`/`payoutType` above
  // stay the single top-weighted family for callers that want one label,
  // but chips and the Payee & type cell use the full breakdown -- a
  // payment is never reduced to just one family.
  familyBreakdown?: {
    types: string[];
    amountCents: number;
    familyKey: string | null;
  }[];
  eventSplit?: {
    amountCents: number;
    eventId: number | null;
    eventName: string | null;
  }[];
  // The QBO account a money-in row lands in (quickbooksControlCenterSource.js
  // moneyInRowToControlCenterItem: the confirmed funding's candidate target).
  paymentAccountContext?: {
    qboAccountId?: string | null;
    qboAccountName?: string | null;
  } | null;
  eventAllocations?: {
    eventId: number | null;
    percent: number | null;
    eventName: string | null;
  }[];
  permissions?: {
    reason?: string;
    canRetry?: boolean;
    canReview?: boolean;
    canSettle?: boolean;
  };
  // A Deposits Fiserv funding parent the worker verified
  // (quickbooksControlCenter.js enrichDurableFundingParentRowsWithClaims):
  // qboEntity stays null and the verified QBO entity is carried here.
  verifiedReadBackEntity?: {
    id: string | null;
    entityType: string | null;
    verifiedAt: string | null;
  } | null;
  categoryIntegrity?:
    | 'ZERO_CATEGORIES'
    | 'EXACTLY_ONE_CATEGORY'
    | 'MULTIPLE_CATEGORIES_NO_ALLOCATION'
    | null;
  project?: {
    name?: string | null;
    id?: string | number | null;
    qboProjectId?: string | null;
    qboProjectName?: string | null;
  } | null;
  // The real item builder (quickbooksControlCenterSource.js's
  // sourceRowToControlCenterItem/mappedEventSummaryItems) never populates
  // the flat `eventId`/`project` fields above -- every item that has one
  // carries it nested here instead, as `{value, name}` for the project ref
  // (not the `project` field's shape). Both fields kept on the type since
  // callers exist for each of the two real shapes this endpoint's rows
  // can take.
  event?: {
    eventId?: number | null;
    eventName?: string | null;
    project?: {
      name?: string | null;
      value?: string | null;
    } | null;
  } | null;
  evidence?: {
    [key: string]: unknown;
    replayVerified?: boolean;
    proofFabricated?: boolean;
    readBackVerified?: boolean;
    sourceFingerprint?: string | null;
  };
  lifecycleMovements?: {
    state: string;
    itemKey: string | null;
    reviewReasons: string[];
    sourceKey: string | null;
    sourceType: string | null;
    amountCents: number | null;
  }[];
  // List-row-only scope integrity. This is deliberately separate from the
  // richer evidence-drawer reviewEligibility contract: an operational queue
  // row may remain visible while its event provenance is insufficient for a
  // safe decision.
  // EVENT_SCOPE_AMBIGUOUS / QB_REVIEW_EVENT_SCOPE_AMBIGUOUS come from the
  // review list's canonical scope binding (quickbooksControlCenter.js
  // bindCanonicalReviewEventScope, :3870-3884).
  scopeIntegrity?: {
    status:
      | 'EXACT_EVENT_SCOPE'
      | 'EVENT_SCOPE_MISSING'
      | 'EVENT_SCOPE_AMBIGUOUS';
    reasonCode:
      | 'QB_REVIEW_EVENT_SCOPE_AMBIGUOUS'
      | 'QB_REVIEW_DECISION_SCOPE_DEFERRED'
      | null;
  };
  auditTrail?: {
    state?: string;
    action?: string;
    result?: string;
    reason?: string;
    eventType?: string;
    id?: string | number;
    proofRefs?: string[];
    actorName?: string | null;
    createdAt?: string | null;
    eventJson?: Record<string, unknown> | null;
  }[];
  sourceContext?: {
    provisional?: boolean;
    label?: string | null;
    [key: string]: unknown;
    componentIds?: number[];
    checkoutId?: number | null;
    paymentType?: string | null;
    businessDate?: string | null;
    siblingTenderTypes?: string[];
    componentCount?: number | null;
    paymentAccountKey?: string | null;
    components?: Array<Record<string, unknown>>;
    moneyInStage?: string | 'CONFIRMED' | 'PROVISIONAL' | 'SOURCE_CONTROL';
  } | null;
  financials?: {
    netCents: number | null;
    grossCents: number | null;
    tipsCents?: number | null;
    cogsCents?: number | null;
    itemsSold?: number | null;
    profitCents: number | null;
    workingDays: number | null;
    salesCount?: number | null;
    expensesCents: number | null;
    salesTaxCents?: number | null;
    shippingCents?: number | null;
    profitPercentage: number | null;
    commissionsCents?: number | null;
    chargebacksCents?: number | null;
    eventPaymentsCents?: number | null;
    processingFeesCents?: number | null;
    reimbursementsCents?: number | null;
    refundMovementsCents?: number | null;
    accountingExpensesCents?: number | null;
    refundedMerchandiseCents?: number | null;
    productRevenuePercentage?: number | null;
    sourceEvidence?: {
      sourceFingerprint: string;
      canonicalCounts: Record<string, number>;
      excludedAliasCounts: Record<string, number>;
      aliases?: Record<
        string,
        Array<{
          aliasId: number;
          reasonCode: string;
          canonicalId: number;
        }>
      >;
    } | null;
  } | null;
}

export interface QuickBooksCurrentStateIdentity {
  runIds: number[];
  available: boolean;
  // The Overview, Needs attention, the Events list, the money lanes and an
  // event page (quickbooksControlCenter.js #servedOverview,
  // #eventsListSource, #laneSource, #servedEventWorkspace): while a newly
  // published revision is still building, the server answers with the
  // previous revision's finished answer. Every other
  // field above then describes that previous revision (its own as-of,
  // revision key and per-event freshness); these three say it is not the
  // current one. reasonCode 'QB_CONTROL_CENTER_BUILDING' (available false)
  // means nothing is built yet and the first build is running.
  updating?: boolean;
  asOf: string | null;
  runId: number | null;
  runKey: string | null;
  realmId: string | null;
  companyId: number | null;
  // Present only when at least one event's current source differs from its
  // last published revision. The surface stays available; those events are
  // waiting for their next check.
  staleEventCount?: number;
  scopeEventCount?: number;
  reasonCode: string | null;
  revisionKey: string | null;
  checkedEventCount?: number;
  // qb-worker pkg5 D1: `asOf` is the OLDEST event check in scope (what
  // freshness reads); `latestAsOf` is the newest one, the time a person means
  // by "the check this view is from". Absent on an older worker.
  latestAsOf?: string | null;
  servedRevision?: 'PREVIOUS';
  // Present only while some events have never been checked (no published
  // revision). The surface shows the checked events; these are listed.
  pendingFirstCheckCount?: number;
  pendingFirstCheckEventIds?: number[];
  scope: Record<string, unknown> | null;
  environment: QuickBooksEnvironment | null;
  runStatus: 'FAILED' | 'CANCELED' | 'SUCCEEDED' | null;
  runMode: 'DRY_RUN' | 'READ_ONLY' | 'WRITE_GATED' | null;
  identityType: 'SINGLE_RUN' | 'COMPOSITE_EVENT_REVISION_SET' | null;
  eventFingerprints: Array<{
    eventId: number;
    sourceFingerprint: string;
  }>;
  currentRevision?: {
    revisionKey: string;
    asOf: string | null;
    latestAsOf?: string | null;
  } | null;
  pendingFirstCheckEvents?: Array<{
    eventId: number;
    eventName: string | null;
    startDate: string | null;
  }>;
  staleEvents?: Array<{
    eventId: number;
    publishedAsOf: string | null;
    publishedRunId: number | null;
    currentSourceFingerprint?: string | null;
    recheck?: QuickBooksStaleEventRecheck | null;
  }>;
  eventRevisions: Array<{
    asOf: string;
    runId: number;
    runKey: string;
    eventId: number;
    runStatus: 'SUCCEEDED';
    sourceFingerprint: string;
    runMode: 'DRY_RUN' | 'READ_ONLY' | 'WRITE_GATED';
  }>;
}

export interface QuickBooksControlCenterList {
  total?: number;
  surface: string;
  // Set with currentState.servedRevision 'PREVIOUS' (see above).
  updating?: boolean;
  asOf: string | null;
  writeMode: 'read_only';
  // pkg6: payouts() 'PAYOUT_DATE_DESC' (newest first), expenses()
  // 'EVENT_CATEGORY'. Absent on older workers.
  listOrder?: string | null;
  filters?: Record<string, unknown>;
  rows: QuickBooksControlCenterItem[];
  fieldGuarantees?: Record<string, unknown>;
  // events() only: events before the closed-books date.
  outOfScope?: QuickBooksEventsOutOfScope | null;
  // deposits() only (pkg6 V6).
  fiservImport?: QuickBooksFiservImportState | null;
  currentState?: QuickBooksCurrentStateIdentity | null;
  // expenses() only (pkg6 V1).
  expenseCategoryTotals?: QuickBooksExpenseCategoryTotal[] | null;
  expenseCategoryReadiness?: QuickBooksExpenseCategoryReadiness[];
  permissions?: {
    canCreate: boolean;
    reasonCode?: string | null;
  };
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  // D1, round-10 follow-up, 2026-08-20 -- real per-chip totals over the
  // full filtered set (quickbooksDepositGrouping.js's depositChipCountsFor()),
  // not the returned page. deposits() only; absent on every other surface.
  depositChipCounts?: {
    all: number;
    found: number;
    review: number;
    failed: number;
    waiting: number;
    assigned: number;
    verified: number;
    notPresent: number;
    sourceOnly: number;
    awaitingFiserv: number;
    confirmedFiserv: number;
  };
  // Expenses tab (round-11) -- same real-per-chip-total pattern as
  // depositChipCounts above, built in from the start rather than found as a
  // D1-style follow-up. expenses() only; absent on every other surface.
  expenseChipCounts?: {
    all: number;
    cash: number;
    review: number;
    failed: number;
    cardBank: number;
    verified: number;
    notPresent: number;
    sourceOnly: number;
    waitingQboPost: number;
    awaitingPayment: number;
    unknownPaymentSource: number;
  };
}

export interface QuickBooksExpenseCategoryReadiness {
  reason: string;
  itemKey: string;
  category: string;
  sourceFamily: string;
  eventId: number | null;
  state: 'EXCLUDED' | 'NOT_PRESENT';
}

// H15, 2026-08-20 -- eventsLegend/waitingForQboPosting/requiresReview/
// deposits/expensesAndPayouts/writeFailures are the real fields
// overview() (quickbooksControlCenter.js) added for PIXEL-MATCH-SPEC.md §3.
// Verified against the actual backend return shape before adding here --
// see the "QB frontend types lie" pattern this codebase has hit before.
export interface QuickBooksControlCenterOverview {
  // Set with currentState.servedRevision 'PREVIOUS' (see above).
  updating?: boolean;
  asOf: string | null;
  setupIssues: string[];
  writeMode: 'read_only';
  metrics: Record<string, number>;
  writeFailures: {
    count: number;
  };
  fieldGuarantees?: Record<string, unknown>;
  // Control Center redesign, step 2a (qb-worker quickbooksControlCenter.js
  // overview(): needsAttentionSummary, bookedThisWeekFrom and
  // recentWorkerActionsFrom in quickbooksNeedsAttention.js). All three are
  // optional: an older server omits them, and the unavailable-current-state
  // health shell returns needsAttention unavailable and bookedThisWeek
  // null. recentWorkerActions is null when the audit read failed -- never
  // an empty list standing in for "the worker did nothing".
  bookedThisWeek?: QuickBooksBookedThisWeek | null;
  currentState?: QuickBooksCurrentStateIdentity | null;
  recentDecisions: QuickBooksControlCenterHistoryRow[];
  eventsNeedingAttention: QuickBooksControlCenterItem[];
  recentWorkerActions?: QuickBooksWorkerAction[] | null;
  needsAttention?: QuickBooksNeedsAttentionSummary | null;
  eventsLegend: {
    active: number;
    blocked: number;
    currentlyReconciled: number;
  };
  controls: {
    enabled: boolean;
    killSwitch: boolean;
    closedThroughDate?: string | null;
  };
  // pkg6 V7: card-sale days not counted in waitingForQboPosting because
  // Fiserv settlements are not imported. Absent on older workers.
  fiservNotImported?: {
    count: number;
    cents: number;
    state?: QuickBooksFiservImportState['state'] | null;
  } | null;
  deposits: {
    totalCount: number;
    verifiedCount: number;
    reviewRequiredCount: number;
    awaitingQboPostCount: number;
    verifiedPercent: number | null;
  };
  expensesAndPayouts: {
    totalCount: number;
    verifiedCount: number;
    reviewRequiredCount: number;
    awaitingQboPostCount: number;
    verifiedPercent: number | null;
  };
  connection: {
    connected: boolean;
    realmId?: string | null;
    companyName?: string | null;
    nextCheckAt?: string | null;
    lastTokenRefreshAt?: string | null;
    pollingIntervalMinutes?: number | null;
    environment?: QuickBooksEnvironment | null;
  };
  requiresReview: {
    itemCount: number;
    eventCount: number;
    amountCents: number;
    durableQueueItemCount?: number;
    derivedPreviewItemCount?: number;
    persistenceState?:
      | 'DURABLE_QUEUE_AVAILABLE'
      | 'DERIVED_PREVIEW_AWAITING_NORMAL_CYCLE';
  };
  // pkg6 V8: the worker's pending source re-checks (PENDING SOURCE_CYCLE
  // outbox rows); backlogged once the oldest waited past boundMinutes. Absent
  // or null when not read (older worker, read failed, scope unavailable).
  recheckBacklog?: {
    pendingCount: number;
    boundMinutes: number;
    backlogged?: boolean;
    oldestPendingAt: string | null;
    oldestAgeMinutes: number | null;
    bySourceType?: {
      sourceType: string;
      pendingCount: number;
      oldestPendingAt: string | null;
    }[];
  } | null;
  waitingForQboPosting: {
    itemCount: number;
    amountCents: number;
    // Status truth (2026-10-16): the same records counted by what the
    // worker's newest QuickBooks lookup found (qb-worker
    // quickbooksSourceLookupStatus.js sourceLookupCounts). Absent on older
    // workers.
    byLookup?: Partial<
      Record<
        QuickBooksSourceLookupStatus,
        { count: number; amountCents: number }
      >
    > | null;
  };
  monitoringHealth?: {
    asOf: string | null;
    proofId: string | null;
    heartbeatFresh: boolean;
    schedulerEnabled: boolean;
    heartbeatIntervalMinutes: number;
    externalNotifications: 'D9_NOT_CONFIGURED';
    health: 'stale' | 'healthy' | 'disabled' | 'attention' | 'unavailable';
    signals: {
      staleWaitCount?: number;
      openDriftCount?: number;
      stuckCycleCount?: number;
      deadLetterCount?: number;
      repeatedFailedCount?: number;
      unavailableSignals?: string[];
    } | null;
  };
  expenseCategoryMappingHealth?: {
    mode: 'automatic';
    proofIds?: string[];
    asOf?: string | null;
    syncRunIds?: number[];
    proofId: string | null;
    onboardingBlocked: false;
    syncRunId?: number | null;
    adapterMode: string | null;
    revisionKey?: string | null;
    unavailableFields: string[];
    lastAttemptAt: string | null;
    openExceptionCount: number | null;
    mappedCategoryCount: number | null;
    presentCategoryCount: number | null;
    unresolvedCategoryCount: number | null;
    allowedUiActions: Array<'retry' | 'inspect' | 'override'>;
    health: 'healthy' | 'partial' | 'exceptions' | 'unavailable';
    sourceFingerprints?: Array<{
      eventId: number;
      sourceFingerprint: string;
    }>;
    liveOpenExceptionDiagnostic?: {
      runBound: false;
      count: number | null;
      affectsCycleHealth: false;
      status: 'LIVE_NON_RUN_BOUND';
    };
    proofScope: {
      eventIds?: number[];
      sourceKeys: string[];
      eventId: number | null;
      tenantComplete: boolean;
      revisionKey?: string | null;
      kind: 'event' | 'source_set' | 'event_revision_set';
      eventProofs?: Array<{
        eventId: number;
        proofId: string;
        syncRunId: number;
        asOf: string | null;
        sourceKeys: string[];
        sourceFingerprint: string;
      }>;
    } | null;
  };
}

// ---------------------------------------------------------------------------
// Needs attention (GET /control-center/needs-attention). Every field below is
// checked against qb-worker/src/services/quickbooksNeedsAttention.js
// (assembleNeedsAttention / finalize / workerHandlingFrom) and
// quickbooksControlCenter.js needsAttention(); a field the server can omit is
// optional here.
export type QuickBooksNeedsAttentionGroupId =
  | 'clix'
  | 'filed'
  | 'lookup'
  | 'project'
  | 'account'
  | 'charges'
  | 'overpay'
  // Duplicate guard #13 reviews: the accountant already booked this on the
  // event's project, but not as CLIX has it (quickbooksNeedsAttention.js
  // NEEDS_ATTENTION_GROUP_TEXT.accountant). Older servers omit it.
  | 'bankfeed'
  // A negative Fiserv day holding money nothing in CLIX explains
  // (quickbooksNeedsAttention.js fundingDecisionFor). Older servers omit it.
  | 'accountant'
  // Status truth (2026-10-16): records the worker's QuickBooks lookup could
  // not tie to exactly one entry, one item per event and kind
  // (quickbooksNeedsAttention.js lookupAttention). Older servers omit it.
  | 'unidentified';

// One record inside a grouped "Can't be found in QuickBooks" item.
export interface QuickBooksNeedsAttentionLookupRecord {
  sourceKey: string;
  fitCount?: number | null;
  checkedAt?: string | null;
  amountCents?: number | null;
  paymentDate?: string | null;
  description?: string | null;
  paymentMethod?: string | null;
  search?: QuickBooksSourceLookup['search'];
  status: string | QuickBooksSourceLookupStatus;
  link?: { type: string; id: number | string } | null;
}

// linkFor(): expense / eventPayment / payout / deposit / event / source; an
// overpay item without a payout id links its review; a Cash project review
// names its record by the source-origin noun (refund / expense / event).
export interface QuickBooksNeedsAttentionLink {
  id: number | string | null;
  type:
    | string
    | 'event'
    | 'payout'
    | 'source'
    | 'review'
    | 'refund'
    | 'expense'
    | 'deposit'
    | 'eventPayment';
}

export interface QuickBooksNeedsAttentionCandidate {
  memo: string | null;
  payee: string | null;
  txnDate: string | null;
  accountId: string | null;
  // V10 (pkg6): the read-only candidate observation adds these.
  docNumber?: string | null;
  qboEntityId: string | null;
  paymentType: string | null;
  amountCents: number | null;
  entityType?: string | null;
  accountName?: string | null;
}

// One decision. Per group the server adds:
//  * project: `record` (the record itself; `link` points at the event);
//  * account: decisionKey, decisionLabel, unmappedPayoutItems;
//  * charges: candidates, candidatesSource, matchReasons;
//  * overpay: payoutId, payoutCents, bankTotalCents, qboEntityId, heldIn
//    (amountCents is then the difference);
//  * bankfeed: evidence, nearest;
//  * filed: payoutId, qboEntityIds, rows (the bank rows already filed to
//    another account), or qboEntityType 'Deposit' for a card deposit
//    someone already split by hand;
//  * accountant: qboEntityIds (the accountant's JournalEntry ids) and rows
//    (its lines: qboEntityId, lineId, docNumber, txnDate, amountCents,
//    accountName), todo;
//  * charges (identical expenses): identicalExpenses;
//  * unidentified: unidentifiedCents (the money nothing identified).
// sinceBasis 'NOT_RECORDED' means no durable row records when the decision
// first appeared: no age is shown, never an age of 0.
export interface QuickBooksNeedsAttentionItem {
  id: string;
  found: string;
  hint?: string;
  // The worker's own "What to do" when it has a specific one (e.g. an
  // expense paid by "Credit": set the card that paid in CLIX).
  todo?: string;
  codes: string[];
  sourceKey: string;
  evidence?: string;
  objectKey?: string;
  // Overpay answered with "What was it?" (qb-worker quickbooksNeedsAttention.js):
  // still listed so the panel can offer Undo, but not counted as a decision.
  recorded?: boolean;
  why?: string | null;
  eventIds?: number[];
  nearest?: unknown[];
  recordCount?: number;
  since: string | null;
  decisionKey?: string;
  decisionLabel?: string;
  eventId?: number | null;
  qboEntityIds?: string[];
  payoutId?: number | null;
  pickRoute?: string | null;
  identicalExpenses?: number;
  amountCents: number | null;
  payoutCents?: number | null;
  qboEntityId?: string | null;
  lastCheckedAt: string | null;
  qboEntityType?: string | null;
  bankTotalCents?: number | null;
  // V10 (pkg6): how many charges fit at the last read (even with no list
  // kept), when, what was searched, and an owner pick route (none today).
  candidateCount?: number | null;
  candidatesReadAt?: string | null;
  unidentifiedCents?: number | null;
  reviewItemIds: Array<number | null>;
  group: QuickBooksNeedsAttentionGroupId;
  link: QuickBooksNeedsAttentionLink | null;
  record?: QuickBooksNeedsAttentionLink | null;
  // group 'lookup' only: the records the item stands for, and its kind.
  records?: QuickBooksNeedsAttentionLookupRecord[];
  candidates?: QuickBooksNeedsAttentionCandidate[];
  // "What was it?" (overpay items only).
  residualLink?: QuickBooksPayoutResidualLink | null;
  residualContext?: QuickBooksOverpayResidualContext;
  kind?: string | 'differs' | 'notFound' | 'severalFit';
  sinceBasis: string | 'NOT_RECORDED' | 'REVIEW_CREATED';
  candidatesSource?: string | 'NOT_RECORDED' | 'ISSUER_REFUSAL_EVENT';
  heldIn?: { accountRef: string | null; accountName: string | null } | null;
  unmappedPayoutItems?: Array<{
    type: string | null;
    category: string | null;
    amountCents: number | null;
  }>;
  candidateSearch?: {
    dateTo: string | null;
    dateFrom: string | null;
    accountId: string | null;
    amountCents: number | null;
    accountName: string | null;
  } | null;
  matchReasons?: {
    rule: string;
    dateTo: string | null;
    dateFrom: string | null;
    tieBreaksTried: string[];
    stageCounts?: Record<string, number> | null;
  } | null;
}

// groups.project.events: one entry per event lacking a project.
export interface QuickBooksNeedsAttentionProjectEvent {
  why: string;
  itemCount: number;
  itemIds: string[];
  sinceBasis: string;
  amountCents: number;
  amountKnown: boolean;
  since: string | null;
  eventId: number | null;
  eventName: string | null;
  linkReview: {
    id: number | null;
    code: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  } | null;
}

// groups.account.mappings: one entry per account mapping a person must add.
export interface QuickBooksNeedsAttentionMapping {
  label: string;
  hint?: string;
  codes: string[];
  itemCount: number;
  objectKey?: string;
  decisionKey: string;
  amountCents: number;
  since: string | null;
  // One entry per CLIX object: every decision key that names it (items keep
  // their own decisionKey), and a proven-account hint when one exists.
  decisionKeys?: string[];
}

export interface QuickBooksNeedsAttentionGroup {
  title: string;
  available: boolean;
  explanation: string;
  // null whenever the group is unavailable -- never 0.
  count: number | null;
  reasonCode: string | null;
  partialReasonCode?: string;
  decisionCount: number | null;
  candidatesPartialReasonCode?: string;
  items: QuickBooksNeedsAttentionItem[];
  mappings?: QuickBooksNeedsAttentionMapping[];
  events?: QuickBooksNeedsAttentionProjectEvent[];
  waitingForQboPosting?: { itemCount: number; amountCents: number } | null;
}

export interface QuickBooksWorkerHandlingCode {
  code: string;
  open: number;
  // Plain words for the code (qb-worker quickbooksReviewCodeLabels.js); null
  // when the server has none. Older servers omit it.
  label?: string | null;
  notRepresented: number;
  // Open rows about records the worker already wrote and proved
  // (quickbooksNeedsAttention.js workerHandlingFrom).
  settledByWrite?: number;
  lastTouchedAt: string | null;
  representedAsDecision: number;
  oldestCreatedAt: string | null;
}

export type QuickBooksWorkerHandling =
  | { available: false; reasonCode: string }
  | {
      total: number;
      basis?: string;
      available: true;
      openReviewTotal: number;
      settledByWriteTotal?: number;
      byCode: QuickBooksWorkerHandlingCode[];
    };

// T-L05: LTL freight next to Expenses (qb-worker quickbooksLtlFreightView.js,
// GET /ltl/freight) and its Needs attention block. Read-only.
export type QuickBooksLtlFreightSourceType = 'STOCK_ORDER' | 'STOCK_TRANSFER';
export interface QuickBooksLtlOpen {
  sourceId: string;
  sourceType: QuickBooksLtlFreightSourceType;
}
// PAID_BY_RUSS_CREDIT (pkg8): a Russ invoice settled by a Russ credit
// against a receivable; no bank payment will ever exist. A newer worker may
// send others: every screen shows an unknown status in its own words.
export type QuickBooksLtlPaymentStatus =
  | 'BOOKED'
  | 'NOT_PAID'
  | 'IN_PAYMENT'
  | 'PAID_BY_RUSS_CREDIT';
export interface QuickBooksLtlFreightSource {
  sourceId: string;
  sourceKey: string;
  sourceLabel: string;
  open: QuickBooksLtlOpen;
  sourceDate: string | null;
  amountCents: number | null;
  attributionStatus: 'EVENT' | 'NO_EVENT';
  sourceType: QuickBooksLtlFreightSourceType;
  events: Array<{ eventId: number; amountCents: number | null }>;
  payment: {
    state: string | null;
    batchId: string | null;
    status: QuickBooksLtlPaymentStatus;
  };
}
export interface QuickBooksLtlFreightInvoice {
  recordCount: number;
  invoiceNumber: string;
  splitIntended: boolean;
  // T-L07: the Russ invoice date from its stored PDF (null without one).
  invoiceDate?: string | null;
  clixPriceCents: number | null;
  sources: QuickBooksLtlFreightSource[];
  eventShares: Array<{ eventId: number; amountCents: number | null }>;
  pdf: {
    state: string;
    message: string | null;
    documentId: string | null;
    receiptUrl: string | null;
    pdfTotalCents: number | null;
  };
  payment: {
    // PAID_BY_RUSS_CREDIT: the worker's sentence for the credit settlement.
    label?: string;
    batchIds: string[];
    status: QuickBooksLtlPaymentStatus;
  };
}
export interface QuickBooksLtlRecordRef {
  sourceKey: string;
  sourceLabel: string;
  open: QuickBooksLtlOpen;
  amountCents: number | null;
  invoiceNumber: string | null;
}
export interface QuickBooksLtlAttentionItem {
  id: string;
  code: string;
  booked?: boolean;
  steps?: string[];
  deleted?: boolean;
  eventIds: number[];
  sourceKey?: string;
  // L11 / L12 (Russ expenses)
  expenseId?: number;
  actions?: string[];
  recordCount?: number;
  ownerParked?: boolean;
  countedTwice?: boolean;
  message?: string | null;
  totalCents?: number | null;
  sourceLabel?: string | null;
  amountCents?: number | null;
  description?: string | null;
  paymentDate?: string | null;
  invoiceNumber?: string | null;
  pdfTotalCents?: number | null;
  clixTotalCents?: number | null;
  paymentMethodId?: number | null;
  countedTwiceWith?: QuickBooksLtlRecordRef[];
  open?: QuickBooksLtlOpen[] | { expenseId: number };
  recordsWithoutInvoiceNumber?: QuickBooksLtlRecordRef[];
}
export type QuickBooksLtlAttentionGroupId =
  | 'noEvent'
  | 'russCard'
  | 'russExpense'
  | 'pdfDifferent'
  | 'priceMissing'
  | 'freightWithoutInvoice'
  | 'invoiceOnSeveralRecords';
export interface QuickBooksLtlAttentionGroup {
  title: string;
  available: boolean;
  explanation: string;
  count: number | null;
  items: QuickBooksLtlAttentionItem[];
  accountantOnly: QuickBooksLtlAttentionItem[];
}
export interface QuickBooksLtlNames {
  events: Record<string, string | null>;
  paymentMethods: Record<string, string | null>;
}
export interface QuickBooksLtlAttention {
  available: boolean;
  laneRead?: boolean;
  count: number | null;
  registerGroups?: boolean;
  tenantAvailable?: boolean;
  names?: QuickBooksLtlNames;
  reasonCode?: string | null;
  order?: QuickBooksLtlAttentionGroupId[];
  groups: Record<
    QuickBooksLtlAttentionGroupId,
    QuickBooksLtlAttentionGroup
  > | null;
}
export interface QuickBooksLtlCreditSettlement {
  label?: string;
  state?: string;
  invoiceNumber: string;
  journalEntryId?: string | null;
  receivable?: {
    booked?: boolean;
    cleared?: boolean;
    invoiceNumber: string;
    amountCents?: number | null;
  } | null;
}
export interface QuickBooksLtlFreight {
  qboTouched: false;
  eventId: number | null;
  tenantAvailable: boolean;
  names: QuickBooksLtlNames;
  attention: QuickBooksLtlAttention;
  // T-L07 (qb-worker quickbooksLtlRussPaymentView.js): the live Russ
  // payments, the company's QuickBooks tenant (the payment routes need it)
  // and which LTL actions the lane flags allow. Older servers omit them.
  payments?: QuickBooksLtlRussPayment[];
  invoices: QuickBooksLtlFreightInvoice[];
  // pkg8 (qb-worker quickbooksLtlFreightView.js loadCreditSettlements): the
  // Russ invoices settled by a Russ credit; journalEntryId once the worker
  // verified the settling journal entry. Older servers omit it.
  creditSettlements?: QuickBooksLtlCreditSettlement[];
  lane?: { read: boolean; rebook: boolean; settlement: boolean };
  tenant?: { realmId: string; environment: QuickBooksEnvironment } | null;
}

// T-L07: one Russ payment as the LTL screen shows it. Every status comes
// from the server; the screen never infers "paid".
export type QuickBooksLtlRussPaymentStatus =
  | 'DRAFT'
  | 'BOOKED'
  | 'BOOKING'
  | 'OWNER_YES'
  | 'PICK_BANK_ROW'
  | 'NEEDS_ATTENTION'
  | 'WAITING_BANK_ROW'
  | 'CORRECTION_PENDING'
  | 'BOOKED_NEEDS_ATTENTION';
export interface QuickBooksLtlPurchaseRow {
  txnDate: string;
  qboEntityId: string;
  amountCents: number | null;
  onClixReview: boolean | null;
}
export interface QuickBooksLtlQboLine {
  lineId: string;
  accountId: string;
  amountCents: number | null;
}
export type QuickBooksLtlRussPaymentAction =
  | { ownerOnly: true; kind: 'TAKEOVER_YES'; rows: QuickBooksLtlPurchaseRow[] }
  | {
      ownerOnly: false;
      kind: 'PICK_BANK_ROW';
      rows: QuickBooksLtlPurchaseRow[];
    }
  | {
      ownerOnly: true;
      kind: 'REBOOK_YES';
      purchaseId: string;
      lines: QuickBooksLtlQboLine[];
    }
  | {
      ownerOnly: true;
      moveCents: number;
      kind: 'ACCOUNTANT_MOVE_YES';
      lines: QuickBooksLtlQboLine[];
      answered: { lineId: string | null } | null;
    };
export interface QuickBooksLtlRussPaymentAttention {
  id: string;
  code: string;
  message: string;
  severity: string;
  openedAt: string | null;
  updatedAt: string | null;
  action: QuickBooksLtlRussPaymentAction | null;
}
export interface QuickBooksLtlRussPayment {
  state: string;
  batchId: string;
  version: number;
  invoiceCount: number;
  bookedAt: string | null;
  createdAt: string | null;
  approvedAt: string | null;
  paymentDate: string | null;
  qboPurchaseId: string | null;
  paymentMethodId: number | null;
  bankAmountCents: number | null;
  pickedTotalCents: number | null;
  paymentMethodName: string | null;
  rail: 'ACH' | 'WIRE' | 'CARD' | null;
  status: QuickBooksLtlRussPaymentStatus;
  attention: QuickBooksLtlRussPaymentAttention[];
  namedPurchase: {
    picked: string | null;
    rebook: string | null;
    takeover: string | null;
  };
  invoices: Array<{
    rows: number;
    retired: boolean;
    amountCents: number | null;
    invoiceNumber: string | null;
  }>;
  correction: {
    id: number | null;
    createdAt: string | null;
    ownerYesLineId: string | null;
    ownerYesApprovedAt: string | null;
    accountantMoveCents: number | null;
  } | null;
}
export interface QuickBooksLtlTenantBody {
  realmId: string;
  environment: QuickBooksEnvironment;
}
export interface QuickBooksRussPaymentFacts {
  paymentDate: string;
  paymentMethodId: number;
  bankAmountCents: number;
  paymentMethod: 'ACH' | 'WIRE';
  exactRemittanceInvoiceNumbers: string[];
}
export interface QuickBooksRussInvoiceUploadFile {
  fileName: string;
  receiptUrl: string;
  contentBase64: string;
}
export interface QuickBooksRussInvoiceCheck {
  message?: string | null;
  invoiceNumber?: string | null;
  pdfTotalCents?: number | null;
  clixTotalCents?: number | null;
  state:
    | 'NO_PDF'
    | 'PDF_DIFFERENT'
    | 'VERIFIED_BY_PDF'
    | 'NOT_IN_REGISTER'
    | 'CLIX_PRICE_MISSING';
}
export interface QuickBooksRussInvoiceUploadResult {
  storedCount: number;
  refusedCount: number;
  alreadyUploadedCount: number;
  results: Array<{
    code?: string;
    message?: string;
    fileName: string | null;
    check?: QuickBooksRussInvoiceCheck;
    state: 'STORED' | 'REFUSED' | 'ALREADY_UPLOADED';
    document?: { totalCents: number; invoiceNumber: string } | null;
  }>;
}

export interface QuickBooksNeedsAttention {
  available: boolean;
  asOf?: string | null;
  generatedAt?: string;
  total: number | null;
  writeMode?: 'read_only';
  reasonCode: string | null;
  surface: 'needs-attention';
  // T-L05: LTL freight (Russ) attention groups; outside `total`. Older
  // servers omit it.
  ltlFreight?: QuickBooksLtlAttention | null;
  // Records of lanes not automated yet (car rentals, LTL freight, records
  // the owner parked): never decisions and never in `total`.
  notAutomated?: QuickBooksNotAutomated | null;
  workerHandling: QuickBooksWorkerHandling | null;
  // Overcharges kept (owner 19 Oct 2026): double charges the owner keeps
  // until the customer asks for a refund; never decisions, never in
  // `total`. Older servers omit it; null when the company has no list.
  overchargesKept?: QuickBooksOverchargesKept | null;
  // Sources the accountant already booked exactly as the worker would
  // (duplicate guard #13 BOOKED): never decisions and never in `total`.
  // Null when the verdict read failed; older servers omit it.
  accountantBooked?: QuickBooksAccountantBooked | null;
  // Records whose every event ends on or before QuickBooks' closed-books
  // date (quickbooksReconciliationScope.js; Owner, 1 October: "2025 we
  // don't need to do"): not reconciled, never decisions, never in `total`.
  outOfScope?: QuickBooksNeedsAttentionOutOfScope | null;
  partial?: { reasonCode: string; failedEventIds: number[] } | null;
  counts: Record<QuickBooksNeedsAttentionGroupId, number | null> | null;
  groups: Record<
    QuickBooksNeedsAttentionGroupId,
    QuickBooksNeedsAttentionGroup
  > | null;
  currentState?: {
    available: boolean;
    staleEventCount: number;
    revisionKey: string | null;
  };
  // Rent payments whose monthly trade-show account the worker creates
  // itself: not decisions, not in `total`. Older servers omit it.
  workerResolvedAccounts?: {
    count: number;
    byCode: Array<{ code: string; count: number }>;
  } | null;
  tenant?: {
    realmId: string | null;
    companyId: number | null;
    environment: QuickBooksEnvironment | null;
  };
  unclassified: {
    count: number;
    byCode: Array<{ code: string; count: number; label?: string | null }>;
  } | null;
  // Pre-write decisions about records the worker already wrote and proved
  // (quickbooksNeedsAttention.js assembleNeedsAttention setAside): not
  // decisions, not in `total`. Older servers omit it.
  settledByWrite?: {
    count: number;
    byCode: Array<{ code: string; count: number }>;
    items: Array<{
      codes: string[];
      sourceKey: string;
      eventIds: number[];
      reviewItemIds: number[];
    }>;
  } | null;
}

export interface QuickBooksNeedsAttentionOutOfScope {
  count: number;
  eventCount?: number;
  source?: string | null;
  closedThroughDate: string | null;
  byGroup?: Record<string, number>;
  byEvent?: Array<{
    eventId: number;
    itemCount: number;
    amountCents: number;
    eventName: string | null;
  }>;
}

// GET /control-center/events: events before the closed-books date, kept out
// of rows, total and counts.
export interface QuickBooksEventsOutOfScope {
  count: number;
  source?: string | null;
  reasonCode?: string | null;
  closedThroughDate: string | null;
  events: Array<{
    state: string | null;
    eventId: number | null;
    eventName: string | null;
    dates: { endDate?: string | null; startDate?: string | null } | null;
  }>;
}

export type QuickBooksNotAutomatedLane =
  | 'ltl'
  | 'carRental'
  | 'accountant'
  // Owner 2026-10-16: cards only the accountant books.
  | 'ownerParked';

export interface QuickBooksNotAutomatedItem {
  id: string;
  why: string;
  found: string;
  codes: string[];
  sourceKey: string;
  sourceType: string;
  eventIds?: number[];
  eventId: number | null;
  category: string | null;
  amountCents: number | null;
  paymentMethod: string | null;
  lane: QuickBooksNotAutomatedLane;
  // FIX-ON-HOLD-PER-LANE (qb-worker quickbooksOwnerParkedLanes.js): on an
  // owner-parked item, the lane it waits for; null = none (Expenses -> On
  // hold). Absent from an older server.
  parkedLane?: 'ltl' | 'carRental' | null;
  link: QuickBooksNeedsAttentionLink | null;
}

// quickbooksNeedsAttention.js accountantBookedFrom: one source's latest
// guard verdict BOOKED, with the accountant's JournalEntry line it rests on.
export interface QuickBooksAccountantBookedItem {
  id: string;
  // "Booked by the accountant — JE <docNumber or id>".
  label: string;
  found: string;
  sourceKey: string;
  eventIds: number[];
  qboEntityId: string;
  lineId: string | null;
  eventId: number | null;
  txnDate: string | null;
  docNumber: string | null;
  recordedAt: string | null;
  amountCents: number | null;
  accountName: string | null;
  lines?: QuickBooksEntryLine[];
  // Status truth (2026-10-16): the entry's own amount and lines, as the
  // filing read holds them (absent on older workers).
  qboAmountCents?: number | null;
  link: QuickBooksNeedsAttentionLink | null;
  // 'Purchase' for an expense or event payment the accountant booked exactly
  // as CLIX has it (qb-worker pkg5 D8, quickbooksAccountantFiling.js).
  qboEntityType: 'Purchase' | 'JournalEntry';
  record: QuickBooksNeedsAttentionLink | null;
}

// One line of a QuickBooks entry as the worker read it.
export interface QuickBooksEntryLine {
  accountId?: string | null;
  projectId?: string | null;
  amountCents?: number | null;
  accountName?: string | null;
  projectName?: string | null;
}

export interface QuickBooksAccountantBooked {
  count: number;
  truncated: boolean;
  items: QuickBooksAccountantBookedItem[];
}

export type QuickBooksOverchargeKeptStatus = 'KEPT' | 'CHECK' | 'REFUND_ASKED';

// qb-worker quickbooksOverchargesKept.js overchargesKeptList.
export interface QuickBooksOverchargeKeptItem {
  id: string;
  key: string;
  eventId: number;
  chargedOn: string;
  amountCents: number;
  customer: string | null;
  eventName: string | null;
  reasonCode: string | null;
  twinCloverPaymentId: string;
  extraCloverPaymentId: string;
  relatedSourceKeys?: string[];
  twinCheckoutId: number | null;
  refundRequestedAt: string | null;
  status: QuickBooksOverchargeKeptStatus;
}

export interface QuickBooksOverchargesKept {
  count: number;
  available: boolean;
  amountCents: number;
  refundedCount: number;
  reasonCode: string | null;
  items: QuickBooksOverchargeKeptItem[];
}

export interface QuickBooksNotAutomated {
  count: number;
  items: QuickBooksNotAutomatedItem[];
  byLane: Record<QuickBooksNotAutomatedLane, number>;
}

// overview.needsAttention: the same assembly, totals only.
export interface QuickBooksNeedsAttentionSummary {
  available: boolean;
  total: number | null;
  reasonCode: string | null;
  partialEventIds: number[];
  unclassifiedCount: number | null;
  counts: Record<QuickBooksNeedsAttentionGroupId, number | null> | null;
  unavailableGroups: Array<{
    reasonCode: string | null;
    group: QuickBooksNeedsAttentionGroupId;
  }>;
  outOfScope?: {
    count: number;
    eventCount?: number;
    closedThroughDate: string | null;
  } | null;
}

// overview.bookedThisWeek (bookedThisWeekFrom). amountCents mixes signs and
// leaves amountUnknownCount sources out, so the Control Center shows only
// the count.
export interface QuickBooksBookedThisWeek {
  count: number;
  basis: string;
  since: string;
  timezone: string;
  amountCents: number;
  weekStartsOn: string;
  amountBasis?: string;
  amountKnownCount: number;
  amountUnknownCount: number;
}

// overview.recentWorkerActions (recentWorkerActionsFrom), newest first.
export interface QuickBooksWorkerAction {
  id: string;
  text: string;
  at: string | null;
  eventType: string;
  sourceKey: string | null;
  qboEntity: { id: string; entityType: string | null } | null;
  kind:
    | string
    | 'write_verified'
    | 'claim_verified'
    | 'recheck_matched'
    | 'reviews_auto_closed'
    | 'replay_already_correct';
}

export interface QuickBooksControlCenterEvidence {
  // Real field on the skeleton (evidenceFor(), quickbooksControlCenter.js
  // -- `state: UNKNOWN_STATE` by default), never overridden downstream
  // for this route -- declared explicitly rather than left to the index
  // signature above so the drawer's header can read it without a cast.
  state?: string;
  itemKey: string;
  sourceKey: string;
  sourceType: string;
  asOf?: string | null;
  [key: string]: unknown;
  qboEntity?: QuickBooksQboEntity | null;
  verifiedEffects?: QuickBooksVerifiedEffect[];
  // Every QBO transaction the worker verified for this item;
  // selectedQboCandidate is its first entry.
  selectedQboCandidates?: QuickBooksQboEntity[];
  laneIsolation?: Record<string, string | boolean>;
  // H5, 2026-08-19 -- the real GET .../items/:itemKey/evidence response
  // (evidenceFor()/enrichEvidenceDrawer(), quickbooksControlCenter.js) never
  // populates the generic `qboEntity` field above; the posted-entity proof
  // is under this name instead. Declared separately rather than folded into
  // `qboEntity` so a reader can tell the two apart -- see evidence-drawer.tsx.
  selectedQboCandidate?: QuickBooksQboEntity | null;
  evidence?: QuickBooksControlCenterItem['evidence'];
  currentState?: QuickBooksCurrentStateIdentity | null;
  auditTrail?: QuickBooksControlCenterItem['auditTrail'];
  currentReviewItem?: {
    id: number;
    code: string;
    status: string;
    updatedAt: string;
  } | null;
  latestReviewDecision?: {
    id: number;
    createdAt: string;
    eventType: string;
    eventJson: Record<string, unknown>;
  } | null;
  reviewEligibility?: {
    canDecide: boolean;
    reasonCode: string | null;
    providerMutationAuthorized: false;
    expectedReviewItemId: number | null;
    expectedReviewUpdatedAt: string | null;
    expectedSourceFingerprint: string | null;
    persistence: 'QbReviewItem+QbPostingLog';
  };
  // Instructor ruling, 2026-08-20 -- deliberately a sibling of `evidence`/
  // `selectedQboCandidate`, never nested inside them. `.facts` is what
  // CLIX knows about its own record (local, safe, always resolvable
  // without a QBO read); `.qbo-card`'s data is what has been read back
  // from QuickBooks -- keeping these two claims structurally separate is
  // the point. null when the item's eventId was not resolvable (no
  // eventId threaded from the caller, or no match found) -- honest
  // absence, never a throw.
  facts?: {
    date: string | null;
    note: string | null;
    merchant: string | null;
    category: string | null;
    eventName: string | null;
    createdAt: string | null;
    receiptUrl: string | null;
    amountCents: number | null;
    employeeName: string | null;
    paymentMethod: string | null;
    createdByName: string | null;
  } | null;
  effectAuthorizationEligibility?: {
    eventId: number;
    realmId: string;
    laneKey: string;
    effectKey: string;
    payloadHash: string;
    canAuthorize: boolean;
    durableEvidence: boolean;
    reasonCode: string | null;
    sourceFingerprint: string;
    rolloutScopePermits: boolean;
    reviewDecisionPermits: boolean;
    deterministicEffectCount: number;
    environment: QuickBooksEnvironment;
    authorizationExpiresAt?: string | null;
    authorizationStatus?:
      | 'ISSUED'
      | 'EXPIRED'
      | 'ELIGIBLE'
      | 'INELIGIBLE'
      | null;
  };
  qboMatchTruth?: {
    queriedAt: string | null;
    queryState: string | null;
    auditEventId: string | number | null;
    // Set with status VERIFIED by #withVerifiedEffectEvidence: the worker's
    // own verified write, or a Fiserv funding parent's durable read-back.
    evidenceKind?: 'WORKER_VERIFIED_EFFECT' | 'DURABLE_VERIFIED_READBACK';
    queryScope: {
      adapterMode: string | null;
      localMatchFilters: Record<string, unknown> | null;
      providerQueryFilters: Record<string, unknown> | null;
    } | null;
    status:
      | 'VERIFIED'
      | 'NOT_CHECKED'
      | 'MATCHED_NOT_VERIFIED'
      | 'NOT_FOUND_AFTER_FRESH_READ'
      | 'UNMATCHED_AFTER_FRESH_READ'
      | 'STALE_OBSERVATION_RECHECK_REQUIRED';
  };
  // Gate 4 task 8: bounded server-side projection of already persisted
  // outbox/checkpoint/query facts. It is operational evidence only; none of
  // these fields assert an accounting mutation, same-ID read-back, or replay.
  automationEvidence?: {
    available: boolean;
    reasonCode: string | null;
    status:
      | 'STALE'
      | 'NOT_RUN'
      | 'INCOMPLETE'
      | 'NOT_CONFIGURED'
      | 'CONFIGURED_EMPTY'
      | 'CONFIGURED_RESULTS';
    checkpoints: {
      effectKey: string;
      checkpoint: string;
      refusalCodes: string[];
      topologyParents: {
        refused: boolean;
        reasonCode: string | null;
        topologyCheckpointVerified: boolean;
      }[];
    }[];
    provenance: {
      companyId?: number;
      sourceKey?: string;
      exactScope: boolean;
      effectCount?: number;
      realmId?: string | null;
      sourceFingerprintMatch: boolean;
      environment?: QuickBooksEnvironment;
      observationAuditEventId?: string | number | null;
    };
    effects?: {
      state: string;
      attempt: number;
      effectKey: string;
      payloadHash: string;
      maxAttempts: number;
      cycleKey: string | null;
      capability: string | null;
      adapterMode: string | null;
      nextAttemptAt: string | null;
      classification: string | null;
      runId: string | number | null;
      deadLetterReason: string | null;
    }[];
    effect: {
      state: string;
      attempt: number;
      effectKey: string;
      payloadHash: string;
      maxAttempts: number;
      cycleKey: string | null;
      capability: string | null;
      adapterMode: string | null;
      nextAttemptAt: string | null;
      classification: string | null;
      runId: string | number | null;
      deadLetterReason: string | null;
    } | null;
    query: {
      complete: boolean;
      truncated: boolean;
      outcome: string | null;
      rowCount: number | null;
      pageSize: number | null;
      maxPages: number | null;
      queriedAt: string | null;
      operation: string | null;
      pagesRead: number | null;
      adapterMode: string | null;
      externallyReadOnly: boolean;
      candidateCounts?: {
        totalRead: number;
        deterministicCandidates: number;
      } | null;
    } | null;
  };
}

// Owner feedback + instructor ruling, 2026-08-20 -- real shape of GET
// /control-center/items/:itemKey/evidence/qbo-facts
// (quickbooks.js's postedTransactionFacts()). Drawer-only, read-only,
// by-id QBO read -- see that method's own header comment. Every field
// honestly null when the real QBO record has no value for it (never an
// empty string to fill the shape).
export interface QuickBooksEvidenceQboFacts {
  txnDate: string | null;
  merchant: string | null;
  accountName: string | null;
  projectName: string | null;
}

// Real shape of GET /control-center/events/:eventId (quickbooksControlCenter.js's
// eventWorkspace()). Field-by-field notes on what's genuinely computed vs.
// hardcoded live with the reading code, not here -- see event-workspace.tsx.
// H4, 2026-08-19 -- real shape of one readiness check
// (quickbooksControlCenter.js's #readinessFor()). A check whose own read
// failed is omitted from `readiness` and named in `readinessUnavailable`
// instead -- never emitted as 'ready' (a fabricated pass) or 'needs_setup'
// (a fabricated failure). Render that distinction; see event-workspace.tsx.
export interface QuickBooksReadinessCheck {
  code: string;
  label: string;
  actionLabel?: string;
  state: 'ready' | 'waiting' | 'blocked' | 'needs_setup';
}

// H4, 2026-08-19 -- real per-lane rollup of the §R9 nine-lane posting
// partition (quickbooksLanePlanner.js's partitionIntoPostingLanes(),
// rolled up by rollUpPostingLanes() in quickbooksControlCenter.js).
// Deliberately surfaced as `postingLanes`, not `lanes` -- PLAN.md's
// documented `lanes` is an unrelated six-key UI-strip contract
// (income|expenses|payouts|products|refunds_chargebacks|financial_controls)
// that needs an owner-ruled taxonomy this codebase does not have yet. Do
// not build against `lanes`; it stays `[]` on purpose.
export interface QuickBooksPostingLane {
  label: string;
  // Always null today -- a source-vs-QBO difference needs a live QBO read
  // this stage never makes. Never render this as 0; that would assert
  // "these agree", which is exactly what is not known.
  difference: null;
  lineCount: number;
  totalCount: number;
  postingLane: string;
  amountCents: number;
  reconciledCount: number;
  actionRequiredCount: number;
  lines: {
    category: string;
    statuses: string[];
    amountCents: number;
    sourceKeys: string[];
  }[];
  durableEvidence?: {
    state: string;
    planId: number;
    sourceKeys: string[];
    movementCount: number;
    reviewReasons: string[];
    sourceRecordCount: number;
    movementAmountCents: number;
    eventAllocationCents: number;
    eventAllocationCount: number;
    sourceFingerprint: string | null;
    qboEntityProofAvailable: boolean;
    batchDetailTotalCents: number | null;
    unresolvedResidualCents: number | null;
    providerComponentTotalCents: number | null;
    fundingEquationDifferenceCents: number | null;
  };
}

export interface QuickBooksControlCenterEventWorkspace {
  eventId: number;
  // Set with currentState.servedRevision 'PREVIOUS': the event's last check,
  // served while its newest one is prepared (see QuickBooksCurrentStateIdentity).
  updating?: boolean;
  asOf: string | null;
  itemsTotal?: number;
  writeMode: 'read_only';
  // Sum of postingLanes' UNPAID_FUTURE_PAYMENT + MAPPING_SOURCE_REVIEW
  // amounts -- "not yet assigned in QBO" per R9's own lane labels.
  unresolvedCents?: number;
  readinessUnavailable: string[];
  items: QuickBooksControlCenterItem[];
  readiness: QuickBooksReadinessCheck[];
  postingLanes: QuickBooksPostingLane[];
  audit?: QuickBooksControlCenterHistoryRow[];
  // pkg6 V9: set on a Dev-held event, even while its current state is
  // unavailable (QB_CURRENT_STATE_FINGERPRINT_EVIDENCE_MISSING).
  heldForOwner?: QuickBooksHeldForOwner | null;
  currentState?: QuickBooksCurrentStateIdentity | null;
  expenseCategoryReadiness?: QuickBooksExpenseCategoryReadiness[];
  refundChargebackMovements?: QuickBooksRefundChargebackMovement[];
  itemsPagination?: { limit: number; offset: number; hasMore: boolean };
  // Round-12, W2-3 -- the same real sync fields overview() already
  // surfaces (lastRefreshedAt/nextScheduledCheckAt/pollingIntervalMinutes),
  // read from the same connection/settings rows the readiness checks
  // already query. null when either read failed -- never a fabricated
  // sync line. No retry field -- nothing in this codebase computes a real
  // one for an event.
  sync?: {
    nextCheckAt?: string | null;
    lastTokenRefreshAt?: string | null;
    pollingIntervalMinutes?: number | null;
  } | null;
  excludedEconomicDuplicates?: Array<{
    itemKey: string;
    sourceKey: string;
    expenseId: number;
    reasonCode: string;
    allocationId: number;
  }>;
  // Round-12 -- the same real grouping the Deposits/Payouts tabs already
  // ship (groupCoreSaleItemsIntoCheckouts/groupPayoutItemsIntoPayments),
  // reused here rather than rebuilt. `items` above stays the flat,
  // ungrouped array (unchanged contract); `workspaceRows` is additive.
  // `expenses`/`products` stay flat -- same as the Expenses tab itself
  // (confirmed no explosion pattern exists in that pipeline).
  workspaceRows: {
    income: QuickBooksControlCenterItem[];
    payouts: QuickBooksControlCenterItem[];
    expenses: QuickBooksControlCenterItem[];
    products: QuickBooksControlCenterItem[];
  };
  expenseReviewSummary?: {
    totalCount: number;
    reasonCounts: Record<string, number>;
    items: Array<{
      sourceKey: string;
      reasonCode: string;
      qboEntityId: string | null;
    }>;
  };
  currentReconciliation: {
    state?: string;
    retryCount?: number;
    reviewReasons?: string[];
    nextRetryAt?: string | null;
    sourceFingerprint?: string | null;
    qboSnapshotVersion?: string | null;
  };
  eventPaymentSourceLedger?: Array<{
    state: string;
    sourceKey: string;
    cashed: boolean | null;
    blockerCodes: string[];
    amountCents: number | null;
    transaction: string | null;
    paymentDate: string | null;
    sourceOrigin: string | null;
    eventPaymentId: number | null;
    paymentMethodId: number | null;
    paymentMethodName: string | null;
  }>;
  reconciliation?: {
    available: boolean;
    unavailableReasonCodes: string[];
    verifiedDifferenceCents: number | null;
    components: Array<{
      component: string;
      sourceCount: number;
      blockerCodes: string[];
      formulaSource?: string | null;
      incompleteCents: number | null;
      correctedCents?: number | null;
      qboVerifiedCents: number | null;
      differenceCents?: number | null;
      providerConfirmed: boolean | null;
      sourceFingerprint?: string | null;
      authoritativeClixCents: number | null;
      beforeCorrectionCents?: number | null;
      beforeCorrectionLabel?: string | null;
      movements?: QuickBooksRefundChargebackMovement[];
    }>;
  };
  event: {
    eventId: number;
    eventName?: string | null;
    heldForOwner?: QuickBooksHeldForOwner | null;
    // Round-12, W2-2 -- real Event.startDate/endDate, already fetched by
    // buildEventSourceInventory, now surfaced instead of discarded.
    dates?: { endDate?: string | null; startDate?: string | null };
    qboProject?: { name?: string | null; value?: string | null } | null;
    convergence: {
      percent?: number;
      totalCount?: number;
      waitingCount?: number;
      reconciledCount?: number;
      sourceOrQboDrift: boolean;
      nextRetryAt?: string | null;
      actionRequiredCount?: number;
      reconciledAsOf?: string | null;
      allCashMovementPosted: boolean;
      sourceFingerprint?: string | null;
      bridgeCurrentlyConverged: boolean;
      qboSnapshotVersion?: string | null;
      genericAutomationDeployed: boolean;
      fullSourceInventoryAccountedFor: boolean;
      nextRetryUnavailableReason?: string | null;
    };
  };
  // Round-12, workspace pixel pass -- the real CLIX P&L for this event
  // (financialsFor() -> eventsService.getProfitAndLoss(), the same
  // service the Overview page's event-summary rows already use for this
  // same event -- PLAN.md :472's own required six figures). Null (honest
  // absence), not a fabricated zero, when the P&L read fails.
  financials?: {
    netCents: number | null;
    grossCents: number | null;
    tipsCents?: number | null;
    cogsCents?: number | null;
    itemsSold?: number | null;
    profitCents: number | null;
    workingDays: number | null;
    salesCount?: number | null;
    expensesCents: number | null;
    salesTaxCents?: number | null;
    shippingCents?: number | null;
    profitPercentage: number | null;
    commissionsCents?: number | null;
    chargebacksCents?: number | null;
    eventPaymentsCents?: number | null;
    processingFeesCents?: number | null;
    reimbursementsCents?: number | null;
    refundMovementsCents?: number | null;
    accountingExpensesCents?: number | null;
    refundedMerchandiseCents?: number | null;
    productRevenuePercentage?: number | null;
    sourceEvidence?: {
      sourceFingerprint: string;
      canonicalCounts: Record<string, number>;
      excludedAliasCounts: Record<string, number>;
      aliases?: Record<
        string,
        Array<{
          aliasId: number;
          reasonCode: string;
          canonicalId: number;
        }>
      >;
    } | null;
  } | null;
  writeProof?: {
    verifiedCount: number;
    sameIdReadBackCount: number;
    replayVerifiedCount: number;
    replacementCreatedCount: number;
    // eventVerifiedWriteProof(), quickbooksControlCenter.js: verified
    // writes per lane. Absent from a backend older than that function.
    laneCounts?: Partial<Record<QuickBooksVerifiedWriteLane, number>>;
    writes: Array<{
      ownerId?: string;
      // `claim:<id>` or `entity-map:<id>` -- the identity of one QBO write.
      // claimId is null for a source-origin JournalEntry.
      writeKey?: string;
      sourceKey: string;
      ownerType?: string;
      qboEntityId: string;
      qboMutation: boolean;
      qboEntityType: string;
      claimId: number | null;
      merchant?: string | null;
      verifiedAt: string | null;
      projectId?: string | null;
      entityMapId?: number | null;
      replayStatus: string | null;
      replacementCreated: boolean;
      verificationAuditId: number;
      amountCents?: number | null;
      projectName?: string | null;
      allocationId?: number | null;
      detailEvidenceStatus?: string;
      syncTokenAfter: string | null;
      paymentMethod?: string | null;
      sourceItemKey?: string | null;
      syncTokenBefore: string | null;
      sameIdReadBackVerified: boolean;
      sourceFingerprint?: string | null;
      authorizationAuditId?: number | null;
      lane?: string | QuickBooksVerifiedWriteLane;
      proofStatus?: string | 'WRITE_VERIFIED' | 'ALREADY_CORRECT';
      paymentAccount?: {
        qboAccountId?: string | null;
        qboAccountName?: string | null;
      } | null;
      expenseAccount?: {
        qboAccountId?: string | null;
        qboAccountName?: string | null;
      } | null;
    }>;
  };
}

export interface QuickBooksRefundChargebackMovement {
  state: string;
  sourceKey: string;
  sourceType: string;
  parentIds: string[];
  batchNumbers: string[];
  blockerCodes: string[];
  refundId: number | null;
  projectId: string | null;
  amountCents: number | null;
  refundPaymentIds: number[];
  providerConfirmed: boolean;
  chargebackId: number | null;
  sourceFingerprints: string[];
  residualCents: number | null;
  lifecycleDisposition: string | null;
  qboEntityProof: Record<string, unknown> | null;
  providerCaseEvidence: Record<string, unknown> | null;
  economicDirection: 'REFUND' | 'RECOVERY' | 'LOSS_OR_OPEN_DEDUCTION';
  accounts: Array<{
    qboAccountId?: string | null;
    qboAccountName?: string | null;
  }>;
  qboEntity: {
    id: string;
    syncToken?: string | null;
    entityType?: string | null;
  } | null;
  verifiedReadBackEntity?: {
    id: string;
    syncToken: string | null;
    entityType: string | null;
    verifiedAt: string | null;
    currentStateObserved: false;
    evidenceKind: 'DURABLE_VERIFIED_READBACK';
  } | null;
}

// Real shape of GET /control-center/events/:eventId/export
// (quickbooksControlCenter.js's eventExport()). `totals` is always `{}` --
// the endpoint never computes it, only `rows` (real, enriched via
// enrichEventExportRows -- unlike eventWorkspace's `items`, these rows do
// carry real qboEntity/auditTrail).
export interface QuickBooksControlCenterEventExport {
  eventId: number;
  asOf: string | null;
  writeMode: 'read_only';
  hardcodedDemoRows: false;
  filters?: Record<string, unknown>;
  rows: QuickBooksControlCenterItem[];
  redaction?: { fields: string[]; applied: boolean };
  currentState?: QuickBooksCurrentStateIdentity | null;
  writeProof?: QuickBooksControlCenterEventWorkspace['writeProof'] | null;
  eventPaymentSourceLedger?: QuickBooksControlCenterEventWorkspace['eventPaymentSourceLedger'];
  totals: {
    rowCount: number;
    amountCents: number;
    waitingCount: number;
    reconciledCount: number;
    actionRequiredCount: number;
  } | null;
  canonicalization?: {
    sourceFingerprint: string;
    canonicalCounts: Record<string, number>;
    excludedAliasCounts: Record<string, number>;
    aliases?: Record<
      string,
      Array<{
        aliasId: number;
        reasonCode: string;
        canonicalId: number;
      }>
    >;
  } | null;
  pnlRows: Array<{
    metric: string;
    blockerCodes?: string[];
    formulaSource?: string | null;
    correctedCents?: number | null;
    differenceCents?: number | null;
    sourceFingerprint: string | null;
    qboVerifiedCents?: number | null;
    authoritativeClixCents: number | null;
    beforeCorrectionCents?: number | null;
    beforeCorrectionLabel?: string | null;
  }>;
  reconciliation?: {
    available: boolean;
    unavailableReasonCodes: string[];
    verifiedDifferenceCents: number | null;
    components: Array<{
      component: string;
      sourceCount: number;
      blockerCodes: string[];
      formulaSource?: string | null;
      incompleteCents: number | null;
      correctedCents?: number | null;
      qboVerifiedCents: number | null;
      differenceCents?: number | null;
      providerConfirmed: boolean | null;
      sourceFingerprint?: string | null;
      authoritativeClixCents: number | null;
      beforeCorrectionCents?: number | null;
      beforeCorrectionLabel?: string | null;
      movements?: QuickBooksRefundChargebackMovement[];
    }>;
  } | null;
}

export type QuickBooksActivityKind =
  | 'write'
  | 'replay'
  | 'detail'
  | 'problem'
  | 'decision';

export interface QuickBooksControlCenterHistoryRow {
  id: string;
  action: string;
  result: string;
  message: string;
  amount?: unknown;
  sentence?: string;
  actorType?: string;
  sourceType: string;
  note?: string | null;
  person?: string | null;
  eventId?: number | null;
  stateTo?: string | null;
  // H1, round-9 follow-up -- null for events with no real per-item
  // identity (e.g. a connection-level admin event), never a raw eventType
  // enum used as a stand-in identifier.
  sourceKey: string | null;
  eventType?: string | null;
  createdAt?: string | null;
  actorName?: string | null;
  stateFrom?: string | null;
  readBackVerified: boolean;
  // qb-worker quickbooksHistoryActivity.js historyActivityFor: the row's
  // kind and its plain sentence (and a person's note / who). Older servers
  // omit them.
  kind?: QuickBooksActivityKind;
  qboEntity?: QuickBooksQboEntity | null;
  // A setting a person changes several times in a row
  // (quickbooksHistoryActivity.js SETTINGS_CHANGE_GROUPS): rows of one key by
  // one person within windowMinutes of each other read as one line.
  settingsGroup?: {
    key: string;
    sentence: string;
    windowMinutes: number;
  } | null;
  automationMetadata?: {
    totalRead: number | null;
    deterministicCandidates: number | null;
  } | null;
}

// -- LTL intake (H9) -------------------------------------------------------
// Real shapes read directly from quickbooksLtlIntake.js and
// quickbooksControlCenter.js#ltlShipment(), not inferred from usage. The
// two existing LTL queries (controlCenterLtlBatch/controlCenterLtlShipment)
// were typed as the unrelated QuickBooksControlCenterEvidence shape -- a
// copy-paste reuse, same class of bug as row.eventId and evidence.evidence
// found earlier this session. controlCenterLtlShipment is corrected below;
// controlCenterLtlBatch is untouched (out of scope for intake).
export type QuickBooksLtlSourceType =
  | 'STOCK_ORDER'
  | 'EVENT_EXPENSE'
  | 'STOCK_TRANSFER';

export interface QuickBooksLtlShipmentRecord {
  state: string;
  version: number;
  shipmentId: string;
  notes?: string | null;
  eventId?: number | null;
  shipDate?: string | null;
  carrierId?: number | null;
  bolNumber?: string | null;
  trackingNumber?: string | null;
  workOrderNumber?: string | null;
  sourceFingerprint?: string | null;
}

export interface QuickBooksLtlInvoiceItemRecord {
  itemKey: string;
  invoiceId: string;
  description: string;
  amountCents: number;
}

export interface QuickBooksLtlInvoiceRecord {
  state: string;
  version: number;
  invoiceId: string;
  shipmentId: string;
  feesCents?: number;
  totalCents: number;
  invoiceNumber: string;
  creditsCents?: number;
  notes?: string | null;
  carrierId?: number | null;
  invoiceDate?: string | null;
  documentUrl?: string | null;
  baseFreightCents?: number | null;
  actualTotalCents?: number | null;
}

export interface QuickBooksLtlSourceLinkRecord {
  sourceId: string;
  shipmentId: string;
  txnDate?: string | null;
  sourceFingerprint?: string | null;
  sourceType: QuickBooksLtlSourceType;
}

export interface QuickBooksLtlAuditEventRecord {
  id: number;
  action: string;
  entityKey: string;
  entityType: string;
  reason?: string | null;
  actorId?: number | null;
  createdAt?: string | null;
  toJson?: Record<string, unknown>;
  fromJson?: Record<string, unknown>;
}

// Real return of GET /control-center/ltl/:shipmentId.
export interface QuickBooksControlCenterLtlShipmentDetail {
  shipmentId: string;
  qboTouched: boolean;
  invoices: QuickBooksLtlInvoiceRecord[];
  items: QuickBooksLtlInvoiceItemRecord[];
  shipment: QuickBooksLtlShipmentRecord | null;
  sourceLinks: QuickBooksLtlSourceLinkRecord[];
  auditEvents: QuickBooksLtlAuditEventRecord[];
  laneIsolation?: Record<string, string | boolean>;
}

// J8, 2026-08-19 -- real shapes read directly from
// quickbooksControlCenter.js's ltlBatch()/#rentalDetail() and the
// QbLtlPaymentBatch/QbRentalBatch model definitions, not inferred. Both
// controlCenterLtlBatch and controlCenterCarRentalBatch were typed as the
// unrelated QuickBooksControlCenterEvidence shape (same copy-paste-reuse
// bug found five times earlier this session) -- corrected below as the
// sixth instance. Batch drawers previously read `batch?.batchId` off that
// wrong generic shape, which is why they always rendered "draft from
// server": the real per-batch detail response has no top-level `batchId`
// display value shaped that way.
export interface QuickBooksLtlPaymentBatchRecord {
  state: string;
  batchId: string;
  version: number;
  retryCount?: number;
  lastError?: string | null;
  totalCents?: number | null;
  approvedAt?: string | null;
  verifiedAt?: string | null;
  paymentDate?: string | null;
  qboAccountId?: string | null;
  approvedById?: number | null;
  varianceCents?: number | null;
  syncTokenBefore?: string | null;
  paymentReference?: string | null;
  postedQboEntityId?: string | null;
  // K4, 2026-08-19 -- migration 20260819110000. Server-computed at draft()
  // from the claimed rows' own sourceFingerprint values, sorted so it
  // depends on the evidence set, not listing order. Null on batches drafted
  // before this existed -- the settle sagas' staleness guard skips rather
  // than fails on absent, and the UI must echo this value back verbatim,
  // never construct or default one.
  sourceFingerprint?: string | null;
  postedQboEntityType?: string | null;
  paymentMethod: 'ACH' | 'WIRE' | 'CARD';
}

export interface QuickBooksLtlBatchItemRecord {
  batchId: string;
  invoiceId: string;
  amountCents?: number | null;
}

// Real return of GET /control-center/ltl/batches/:batchId
// (quickbooksControlCenter.js's ltlBatch()).
export interface QuickBooksControlCenterLtlBatchDetail {
  itemKey: string;
  batchId: string;
  sourceKey: string;
  sourceType: string;
  tenant?: Record<string, unknown>;
  invoices: QuickBooksLtlBatchItemRecord[];
  batch: QuickBooksLtlPaymentBatchRecord | null;
  laneIsolation?: Record<string, string | boolean>;
}

export interface QuickBooksRentalBatchRecord {
  state: string;
  version: number;
  batchKey: string;
  totalCents?: number | null;
  approvedAt?: string | null;
  verifiedAt?: string | null;
  // K3/K4, 2026-08-19 -- migrations 20260819100000/20260819110000, mirroring
  // the LTL batch fields above. qboAccountId/paymentDate are what
  // buildRentalCandidateTarget needs to search for the posted transaction;
  // sourceFingerprint is server-computed at draft() and must be echoed back
  // verbatim on settle, never constructed.
  paymentDate?: string | null;
  approvedById?: number | null;
  qboAccountId?: string | null;
  syncTokenBefore?: string | null;
  paymentReference?: string | null;
  postedQboEntityId?: string | null;
  sourceFingerprint?: string | null;
  postedQboEntityType?: string | null;
  paymentMethod?: 'ACH' | 'WIRE' | 'CARD' | null;
  vendor: 'AVIS' | 'MIXED' | 'BUDGET' | 'ENTERPRISE';
}

// Real return of GET /control-center/car-rentals/batches/:batchId
// (quickbooksControlCenter.js's #rentalDetail()). The route param is
// literally named ':batchId' but is used to look up by the batchKey
// column -- the raw row lands under `record`, not `batch`.
export interface QuickBooksControlCenterRentalBatchDetail {
  itemKey: string;
  sourceKey: string;
  sourceType: string;
  tenant?: Record<string, unknown>;
  record: QuickBooksRentalBatchRecord | null;
  laneIsolation?: Record<string, string | boolean>;
}

export interface QuickBooksLtlShipmentCreateRequest {
  sourceId: string;
  realmId?: string;
  notes?: string | null;
  eventId?: number | null;
  shipDate?: string | null;
  carrierId?: number | null;
  bolNumber?: string | null;
  trackingNumber?: string | null;
  workOrderNumber?: string | null;
  sourceType: QuickBooksLtlSourceType;
  environment?: QuickBooksEnvironment;
}

export interface QuickBooksLtlShipmentCreateResult {
  state: string;
  writeMode: string;
  shipmentId: string;
  qboTouched: boolean;
  link: QuickBooksLtlSourceLinkRecord;
  shipment: QuickBooksLtlShipmentRecord;
  source: {
    sourceId: string;
    sourceType: string;
    sourceLabel: string;
    eventId: number | null;
    txnDate: string | null;
    freightCents: number | null;
  };
}

export interface QuickBooksLtlShipmentEditRequest {
  reason?: string;
  realmId?: string;
  notes?: string | null;
  expectedVersion: number;
  eventId?: number | null;
  shipDate?: string | null;
  carrierId?: number | null;
  bolNumber?: string | null;
  trackingNumber?: string | null;
  workOrderNumber?: string | null;
  environment?: QuickBooksEnvironment;
}

export interface QuickBooksLtlShipmentEditResult {
  state: string;
  version: number;
  writeMode: string;
  shipmentId: string;
  qboTouched: boolean;
  shipment: QuickBooksLtlShipmentRecord;
}

export interface QuickBooksLtlInvoiceItemInput {
  itemKey?: string;
  description: string;
  amountCents: number;
}

export interface QuickBooksLtlInvoiceCreateRequest {
  realmId?: string;
  feesCents?: number;
  invoiceNumber: string;
  creditsCents?: number;
  notes?: string | null;
  carrierId?: number | null;
  invoiceDate?: string | null;
  documentUrl?: string | null;
  baseFreightCents?: number | null;
  actualTotalCents?: number | null;
  environment?: QuickBooksEnvironment;
  items?: QuickBooksLtlInvoiceItemInput[];
}

export interface QuickBooksLtlInvoiceEditRequest extends Partial<QuickBooksLtlInvoiceCreateRequest> {
  reason?: string;
  expectedVersion: number;
}

export interface QuickBooksLtlInvoiceWriteResult {
  state: string;
  version?: number;
  invoiceId: string;
  writeMode: string;
  totalCents: number;
  shipmentId?: string;
  qboTouched: boolean;
  varianceCents: number | null;
  invoice: QuickBooksLtlInvoiceRecord;
  items: QuickBooksLtlInvoiceItemRecord[];
  shipmentState?: { to: string; from: string } | null;
}

export interface QuickBooksControlCenterLtlInvoiceDetail {
  invoiceId: string;
  batches: unknown[];
  qboTouched: boolean;
  varianceCents: number | null;
  invoice: QuickBooksLtlInvoiceRecord;
  items: QuickBooksLtlInvoiceItemRecord[];
  auditEvents: QuickBooksLtlAuditEventRecord[];
}

export interface QuickBooksControlCenterDryRunRequest {
  reason?: string;
  sourceType?: string;
  expectedSourceFingerprint: string;
}

// Round-12, evidence drawer rebuild -- the real #classify() response
// (quickbooksCandidateDryRun.js:375-460) never returns `candidates`; that
// field name was never real, same class of drift as evidence.selectedQboCandidate
// vs qboEntity found earlier this session. The real fields are named below.
// `readAdapterMode` is the honest signal for the auto-candidate gate: today
// it is always 'not_configured_local_empty' (defaultReadAdapter, Item 3 --
// no live QBO read is wired for automatic candidate search yet), so
// `qboCandidates` is always `[]` and must render as a gated state, never an
// empty-and-broken-looking picker.
export interface QuickBooksControlCenterDryRunResult {
  state: string;
  itemKey?: string;
  eventId?: number;
  qboTouched?: boolean;
  writeAttempted: false;
  readAdapterMode?: string;
  reviewReasons?: string[];
  configuredEmpty?: boolean;
  qboCandidates?: QuickBooksMoneyOutCandidate[];
  selectedQboCandidate?: QuickBooksMoneyOutCandidate | null;
  candidateCounts?: {
    totalRead: number;
    dateMatches: number;
    amountMatches: number;
    accountMatches: number;
    directionMatches: number;
    deterministicCandidates: number;
  };
  claimEligibility?: {
    claimId?: number;
    reasonCode?: string;
    claimAcquired: false;
    state:
      | 'ELIGIBLE_NEW'
      | 'ALREADY_VERIFIED'
      | 'CONTENDED_REVIEW_REQUIRED'
      | 'ELIGIBLE_IDEMPOTENT_REPLAY'
      | 'EXPLICIT_TRANSITION_REQUIRED'
      | 'PARENT_IDENTITY_PROOF_REQUIRED';
  } | null;
  eventScope?: {
    eventId: number;
    sourceFingerprint: string;
    controls: Array<{ code: string; state: string }>;
    lanes: Array<{
      lineCount: number;
      postingLane: string;
      amountCents: number;
    }>;
    counts: {
      total: number;
      waiting: number;
      reconciled: number;
      actionRequired: number;
    };
  };
  queryEvidence?: {
    realmId: string;
    companyId: number;
    queriedAt: string;
    entityTypes: string[];
    providerCandidateCount: number;
    environment: QuickBooksEnvironment;
    providerQueryFilters: Record<string, string | number | null>;
    queryOutcome:
      | 'CONFIGURED_PROVIDER_QUERY_EMPTY'
      | 'CONFIGURED_PROVIDER_QUERY_NONEMPTY';
    matchStageCounts: {
      dateMatches: number;
      amountMatches: number;
      accountMatches: number;
      providerReturned: number;
      directionMatches: number;
      deterministicCandidates: number;
    };
    localMatchFilters: {
      dateTo: string;
      dateFrom: string;
      amountCents: number;
      merchant: string | null;
      direction: string | null;
      accountId: string | null;
      merchantAliases: string[];
      accountLabel: string | null;
      accountLast4: string | null;
      requiredIdentityTerms: string[];
    };
  } | null;
}

export interface QuickBooksControlCenterReviewDecisionRequest {
  reason: string;
  expectedReviewItemId?: number;
  expectedReviewUpdatedAt?: string;
  expectedSourceFingerprint: string;
  // Matches the backend's `controlCenterReviewDecisionBody` field name
  // exactly (`action`, not `decision`) -- the body schema is
  // `additionalProperties: false` with `action` required, so a mismatched
  // key here is a guaranteed 400, not a style choice.
  action:
    | 'retry_later'
    | 'mark_unrelated'
    | 'mapping_required'
    | 'accept_candidate_for_review';
}

export interface QuickBooksControlCenterEffectAuthorizationRequest {
  realmId: string;
  eventId: number;
  laneKey: string;
  effectKey: string;
  payloadHash: string;
  expectedSourceFingerprint: string;
  environment: QuickBooksEnvironment;
}

export interface QuickBooksControlCenterEffectAuthorizationResult {
  issued: true;
  laneKey: string;
  identity: string;
  effectKey: string;
  expiresAt: string;
  sourceKey: string;
  coalesced: boolean;
  authorizationId: string;
}

export interface QuickBooksLtlBatchActionRequest {
  reason?: string;
  invoiceIds?: string[];
  idempotencyKey: string;
  expectedVersion: number;
  expectedSourceFingerprint: string;
}

// J5, 2026-08-19 -- distinct from QuickBooksLtlBatchActionRequest above.
// settlementEditBody's `required` is only expectedVersion (clix-backend@8d07c9e
// dropped expectedSourceFingerprint from it -- neither QbLtlPaymentBatch nor
// QbRentalBatch has that column, so no caller could ever produce a real
// value). expectedSourceFingerprint stays accepted, not required: if a batch
// ever gains a real fingerprint the sagas' currently-inert guard starts
// firing, and an omitted value then fails closed rather than silently
// matching. Never send a fabricated one to satisfy this type.
export interface QuickBooksLtlBatchEditRequest {
  reason?: string;
  expectedVersion: number;
  paymentDate?: string | null;
  qboAccountId?: string | null;
  paymentReference?: string | null;
  expectedSourceFingerprint?: string;
  exactRemittanceTotalCents?: number;
  paymentMethod?: 'ACH' | 'WIRE' | 'CARD';
  exactRemittanceInvoiceNumbers?: string[];
}

// K4, 2026-08-19 -- real shape of settlementSettleBody (schemas/quickbooks.js),
// shared verbatim by both the LTL and car-rental settle routes. Unlike J5's
// edit fix, expectedSourceFingerprint stays genuinely required by the server
// schema here -- it seeds ltlSettlementReplayKey's/the rental saga's
// idempotency key, which a version number cannot answer ("has the evidence
// UNDER the batch changed", not "has the batch row changed"). Typed optional
// here anyway: a batch drafted before migration 20260819110000 has no real
// fingerprint, and the caller must send none rather than fabricate one --
// the resulting 400 from the server is the honest answer for that batch, not
// something this type should paper over.
export interface QuickBooksBatchSettleRequest {
  txnDate: string;
  expectedVersion: number;
  exceptionalAction?: boolean;
  expectedSourceFingerprint?: string;
}

// K4, 2026-08-19 -- assertPostedCandidateReady (both settlement sagas) now
// attaches the candidate lock's real reason to a 423's `details` instead of
// discarding it (clix-backend@6b1af68, error handler serializes it per
// clix-backend@33908d4). `reason` is a code, not copy -- map it to
// accountant-readable text client-side, never render the constant.
export interface QuickBooksCandidateLockRefusalDetails {
  candidateCount?: number;
  reason:
    | string
    | 'REVIEW_ZERO_CANDIDATES'
    | 'NO_BATCH_TOTAL_EVIDENCE'
    | 'NO_PAYMENT_DATE_EVIDENCE'
    | 'REVIEW_MULTIPLE_CANDIDATES'
    | 'NO_ACCOUNT_IDENTITY_EVIDENCE';
}

export interface QuickBooksCarRentalActionRequest {
  reason?: string;
  idempotencyKey: string;
  expectedVersion: number;
  rentalActualIds?: string[];
  expectedSourceFingerprint: string;
}

// T-R04: rental statements (T-R02 GET /rentals/statements) and the
// "Confirm statement" save plan (GET /rentals/statements/:id/save-plan).
// Integer cents throughout; the worker's own field names.
export type QuickBooksRentalVendorGroup = 'ENTERPRISE' | 'AVIS_BUDGET';

export interface QuickBooksRentalStatement {
  id: string;
  state: string;
  receiptUrl: string;
  isCutover?: boolean;
  completeness: string;
  statementDate: string;
  fileName: string | null;
  includesAllAccounts: boolean;
  vendorGroup: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalStatementList {
  statements: QuickBooksRentalStatement[];
  // T-R08: a same-date upload with a different file waits for an answer
  // (POST /rentals/statements/:id/conflict).
  openConflicts?: QuickBooksRentalOpenConflict[];
}

export interface QuickBooksRentalItemFacts {
  vendor: string | null;
  itemKey: string | null;
  itemDate: string | null;
  renterName: string | null;
  currentCents: number | null;
  documentClass: string | null;
  originalCents: number | null;
  billingAccount: string | null;
  documentNumber: string | null;
  agreementNumber: string | null;
  reservationNumber: string | null;
}

// The T-F03 "Save to CLIX" proposal exactly as the worker built it.
export interface QuickBooksRentalProposalSave {
  proposalId: string;
  amountCents: number;
  description?: string;
  paymentMethodId?: number;
  basisAmountCents?: number;
  expenseId?: number | null;
  receiptUrl?: string | null;
  paymentDate?: string | null;
  rentalDetail?: Record<string, unknown> | null;
  categories?: { categoryId: number; amountCents?: number }[];
  events?: { eventId: number; percent: number; amountCents?: number }[];
}

export type QuickBooksRentalSaveKind =
  | 'FOLLOW_UP'
  | 'MARK_TOLL'
  | 'FINAL_AMOUNT'
  | 'FOLLOW_UP_CORRECTION';

export interface QuickBooksRentalSaveStep {
  order: number;
  proposalId: string;
  proposalKey: string;
  tollIdentified: boolean;
  expenseId: number | null;
  amountCents: number | null;
  followupKind: string | null;
  state: 'SAVED' | 'PROPOSED';
  savedExpenseId: number | null;
  kind: QuickBooksRentalSaveKind;
  parentExpenseId: number | null;
  currentAmountCents: number | null;
  save: QuickBooksRentalProposalSave;
  item: QuickBooksRentalItemFacts | null;
  mismatch: { differences?: { field: string }[] } | null;
}

export interface QuickBooksRentalNewRentalPrefill {
  amountCents: number;
  description?: string;
  rentalCompany: string;
  itemDate: string | null;
  renterName: string | null;
  receiptUrl: string | null;
  paymentDate: string | null;
  agreementNumber: string | null;
  paymentMethodId: number | null;
  reservationNumber: string | null;
}

export interface QuickBooksRentalNewRental {
  proposalId: string;
  proposalKey: string;
  amountCents: number | null;
  item: QuickBooksRentalItemFacts | null;
  prefill: QuickBooksRentalNewRentalPrefill | null;
}

export interface QuickBooksRentalNotSaved {
  kind: string;
  proposalId: string;
  proposalKey: string;
  expenseId: number | null;
  amountCents: number | null;
  blockerCode: string | null;
  item: QuickBooksRentalItemFacts | null;
  reason: 'BLOCKED' | 'NOT_BOOKED' | 'NEEDS_ANSWER';
}

export interface QuickBooksRentalSavePlan {
  steps: QuickBooksRentalSaveStep[];
  statement: QuickBooksRentalStatement;
  notSaved: QuickBooksRentalNotSaved[];
  newRentals: QuickBooksRentalNewRental[];
  counts: {
    steps: number;
    finals: number;
    notSaved: number;
    followUps: number;
    newRentals: number;
    alreadySaved: number;
  };
}

export interface QuickBooksRentalDuplicateCheck {
  expenseId: number | null;
  kind: 'NONE' | 'CONFIRMED';
}

// T-R08: the car-rental screens (worker routes behind qb.carRental.import;
// 423 when off). Integer cents and the worker's own field names: T-R02
// statements, T-R03 proposals, T-R06 accounts and cutover review, T-R07
// payments, and GET /rentals/lane (quickbooksRentalLaneView.js).
export interface QuickBooksRentalOpenConflict {
  statementId: string;
  statementDate: string;
  earlierStatementId: string | null;
  vendorGroup: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalStatementItem {
  id: string;
  itemKey: string;
  status?: string;
  itemClass: string;
  currentCents: number;
  vendor: string | null;
  originalCents: number;
  itemDate: string | null;
  billingAccountId: string;
  renterName: string | null;
  documentClass: string | null;
  documentNumber: string | null;
  reservationNumber: string | null;
  parentDocumentNumber: string | null;
}

export interface QuickBooksRentalStatementAccount {
  rowCount: number;
  openCents: number;
  completeness: string;
  clearedCents: number;
  vendor: string | null;
  statementDate: string;
  billingAccountId: string;
  clearedItemCount: number;
  accountNumber: string | null;
  items?: QuickBooksRentalStatementItem[];
  clearedItems?: QuickBooksRentalStatementItem[];
}

// Lines first seen on a short Enterprise export: not stored until a full
// statement shows them (RENTAL_ITEM_NEEDS_FULL_EXPORT).
export interface QuickBooksRentalWaitingForFullExport {
  cents: number;
  count: number;
  items: Array<{
    code: string;
    currentCents: number;
    documentNumber: string;
    itemDate: string | null;
    billingAccountId?: string | null;
  }>;
}

export interface QuickBooksRentalStatementDetail {
  statement: QuickBooksRentalStatement;
  accounts: QuickBooksRentalStatementAccount[];
  waitingForFullExport?: QuickBooksRentalWaitingForFullExport;
  totals: { accounts: number; rowCount: number; openCents: number };
}

export interface QuickBooksRentalUploadRequest {
  fileName: string;
  receiptUrl: string;
  statementDate: string;
  contentBase64: string;
  includesAllAccounts: boolean;
  confirmDateTooLate?: boolean;
  confirmDateMismatch?: boolean;
  vendor: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalUploadResult {
  alreadyStored?: boolean;
  statement?: QuickBooksRentalStatement;
  accounts?: QuickBooksRentalStatementAccount[];
  waitingForFullExport?: QuickBooksRentalWaitingForFullExport;
  totals?: { accounts: number; rowCount: number; openCents: number };
  proposals?: {
    summary?: Record<string, number>;
    summaryByGroup?: Partial<
      Record<QuickBooksRentalVendorGroup, Record<string, number>>
    >;
  } | null;
}

export interface QuickBooksRentalSafetyNet {
  status: string;
  expectedCents?: number;
  owedCents?: number | null;
  statementDate: string | null;
  statementBalanceCents?: number;
  remainderCents?: number | null;
  differenceCents?: number | null;
  causes?: Array<{
    code: string;
    cents?: number;
    count?: number;
    pairingId?: string;
    expenseIds?: number[];
  }>;
}

export interface QuickBooksRentalAccountBalance {
  vendor: string;
  accountNumber: string;
  // Open items on the same statement as openBalanceCents (D2).
  openItemCount?: number;
  billingAccountId: string;
  openBalanceCents: number;
  lastPayment: { id: string; date: string; amountCents: number } | null;
  // The account's snapshot on the vendor group's balance statement (the
  // latest COMPLETE one; DEV-FUNCTIONAL-PKG2 D2).
  lastStatement: {
    statementId: string;
    completeness: string;
    statementDate: string;
  } | null;
  oldestOpenItem: {
    currentCents: number;
    documentNumber: string;
    itemDate: string | null;
  } | null;
  // A newer snapshot than that one (an INCOMPLETE file): shown, never added.
  latestUpload?: {
    openCents: number;
    statementId: string;
    completeness: string;
    statementDate: string;
  } | null;
}

// A statement newer than the balance statement that is INCOMPLETE (a short
// Enterprise export with lines waiting for the full statement, or a file
// uploaded as partial). It never changes the vendor balance, our accrued
// balance or the difference (05 §A (c), §E).
export interface QuickBooksRentalNewerPartialStatement {
  openCents: number;
  statementId: string;
  waitingCount: number;
  waitingCents: number;
  statementDate: string;
  completenessReason: string | null;
}

export interface QuickBooksRentalVendorBalance {
  label: string;
  paidAheadCents: number;
  owedAsOf: string | null;
  owedCents: number | null;
  // DEV-FUNCTIONAL-PKG2 D2: the one statement the vendor
  // balance, accrued balance and difference are all as of. Absent from an
  // older worker.
  balanceAsOf?: string | null;
  safetyNetSameBasis?: boolean;
  differenceCents?: number | null;
  // As of balanceAsOf: the latest COMPLETE statement (null without one).
  vendorBalanceCents: number | null;
  balanceStatementId?: string | null;
  vendorGroup: QuickBooksRentalVendorGroup;
  accounts: QuickBooksRentalAccountBalance[];
  safetyNet: QuickBooksRentalSafetyNet | null;
  newerPartialStatement?: QuickBooksRentalNewerPartialStatement | null;
  lastPayment: { id: string; date: string; amountCents: number } | null;
  cutover: {
    origin: string;
    statementId: string;
    cutoverDate: string;
    openingCents: number;
  } | null;
}

export interface QuickBooksRentalAccounts {
  groups: QuickBooksRentalVendorBalance[];
}

export interface QuickBooksRentalD8aLine {
  id: string;
  expenseId: number;
  actions: string[];
  paymentRefs: string[];
  replacedByLink: boolean;
  pairingId: string | null;
  amountCents: number | null;
  paymentDate: string | null;
  decision: 'ACCRUE' | 'EXCLUDE' | null;
}

export interface QuickBooksRentalD8bLine {
  id: string;
  gap: boolean;
  txnDate: string;
  reasons: string[];
  actions: string[];
  purchaseId: string;
  handAdded: boolean;
  amountCents: number;
  memo: string | null;
  payee: string | null;
  decision: string | null;
  excludedReason: string | null;
  // null: the row names no single vendor (an Auto Rental line, or both
  // names); it is listed under both and the answer names the vendor.
  vendorGroup?: QuickBooksRentalVendorGroup | null;
}

export interface QuickBooksRentalRelief {
  lineId: string;
  itemKey: string;
  booked: boolean;
  reliefId: string;
  itemClass: string;
  actions: string[];
  accruedCents: number;
  lastOpenCents: number;
  proposedCents: number;
  documentNumber: string;
  fellSinceCutover: boolean;
  decisionId: string | null;
  clearedDate: string | null;
  decisionAmountCents: number | null;
}

export interface QuickBooksRentalReviewGroup {
  label: string;
  vendorGroup: QuickBooksRentalVendorGroup;
  d13?: {
    items: string[];
    accounts: string[];
    reliefs: QuickBooksRentalRelief[];
  };
  cutover: {
    statementId: string;
    cutoverDate: string;
    openingCents: number;
  } | null;
  r18?: {
    id: string;
    booked: boolean;
    openingCents: number;
    decision: string | null;
  };
  d8b?: {
    windowStart: string;
    windowEnd: string | null;
    lines: QuickBooksRentalD8bLine[];
  };
  residuals?: Array<{
    code: string;
    cents: number;
    pairingId: string;
    expenseIds: number[];
  }>;
  d8a?: {
    waitingOnLinks: number;
    waitingMessage: string | null;
    lines: QuickBooksRentalD8aLine[];
  };
  notRecorded?: Array<{
    id: string;
    txnDate: string;
    actions: string[];
    purchaseId: string;
    amountCents: number;
    payee: string | null;
  }>;
  d12?: {
    decided: Array<{
      booked: boolean;
      noEventKey: string;
      amountCents: number;
    }>;
    offers: Array<{
      id: string;
      kind: string;
      category: string;
      proposalId: string;
      noEventKey: string;
      amountCents: number;
    }>;
  };
}

export interface QuickBooksRentalCutoverReview {
  groups: QuickBooksRentalReviewGroup[];
}

export interface QuickBooksRentalDecideRequest {
  lineId: string;
  decision: string;
  amountCents?: number;
  paymentRefs?: string[];
  pairWithExpenseIds?: number[];
  vendorGroup?: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalPayment {
  id: string;
  date: string;
  state: string;
  version: number;
  exception: string;
  amountCents: number;
  bookedAt: string | null;
  reference: string | null;
  purchaseId: string | null;
  waitReason: string | null;
  receiptUrl: string | null;
  cancelledAt: string | null;
  qboPurchaseId: string | null;
  bookedOwedCents: number | null;
  paymentMethodId: number | null;
  billingAccountId: string | null;
  vendorGroup: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalPaymentRequest {
  reference?: string;
  amountCents: number;
  paymentDate: string;
  receiptUrl?: string;
  paymentMethodId: number;
  billingAccountId?: string;
  vendorGroup: QuickBooksRentalVendorGroup;
}

export interface QuickBooksRentalLaneRental {
  expenseId: number;
  itemKey: string | null;
  renterName: string | null;
  amountCents: number | null;
  paymentDate: string | null;
  documentNumber: string | null;
  parentExpenseId: number | null;
  paymentMethodId: number | null;
  reservationNumber: string | null;
  kind: 'BASE' | 'FOLLOW_UP' | null;
  vendorGroup: QuickBooksRentalVendorGroup | null;
  events: Array<{
    eventId: number;
    percent: number | null;
    eventName: string | null;
  }>;
  accrual: {
    state: string | null;
    reason: string | null;
    amountCents: number | null;
    bookedCents: number | null;
  };
}

export interface QuickBooksRentalPaymentAttention {
  code: string;
  paymentId: string;
  candidates: Array<{
    txnDate: string;
    qboEntityId: string;
    amountCents: number | null;
    onClixReview: boolean | null;
  }>;
}

export interface QuickBooksRentalLane {
  rentals: QuickBooksRentalLaneRental[];
  directBilling: Record<string, number[]>;
  paymentAttention: QuickBooksRentalPaymentAttention[];
  mappings: Record<
    string,
    { owed: boolean; openingOffset: boolean; directBilling: boolean }
  >;
  bookedEntries: Array<{
    entryKey: string;
    amountCents: number;
    txnDate: string | null;
    vendorGroup: QuickBooksRentalVendorGroup;
  }>;
}

export interface QuickBooksRentalProposal {
  kind: string;
  state: string;
  actions: string[];
  proposalId: string;
  itemKeys: string[];
  tollIdentified: boolean;
  expenseId: number | null;
  noEventKey: string | null;
  statementId: string | null;
  amountCents: number | null;
  blockerCode: string | null;
  followupKind: string | null;
  savedExpenseId: number | null;
  parentExpenseId: number | null;
  billingAccountId: string | null;
  currentAmountCents: number | null;
  noEventAmountCents: number | null;
  vendorGroup: QuickBooksRentalVendorGroup;
  save: QuickBooksRentalProposalSave | null;
  evidence: Record<string, unknown> & {
    item?: QuickBooksRentalItemFacts | null;
  };
}

export interface QuickBooksRentalProposals {
  summary?: Record<string, number>;
  proposals: QuickBooksRentalProposal[];
}

const setupRequestHeaders = { 'allow-not-found': 'true' };

// The cached whole list of one `allPages` argument set (set below, once the
// API exists; typed on its own so the endpoint's types do not depend on
// themselves).
let selectCachedControlCenterList:
  | ((arg: object, state: unknown) => QuickBooksControlCenterList | undefined)
  | null = null;
const cachedControlCenterList = (arg: object, state: unknown) =>
  selectCachedControlCenterList?.(arg, state);

export const quickBooksApi = createApi({
  reducerPath: 'quickBooksApi',
  baseQuery: quickBooksApiQuery,
  tagTypes: [
    'QuickBooksConnection',
    'QuickBooksMappings',
    'QuickBooksProjects',
    'QuickBooksRuns',
    'QuickBooksSettings',
    'QuickBooksControlCenter',
  ],
  endpoints: (build) => ({
    rescanRentals: build.mutation<{ rescanned: boolean }, void>({
      query: () => ({
        data: {},
        method: 'post',
        url: 'quickbooks/rentals/rescan',
      }),
    }),
    runDetails: build.query<Record<string, unknown>, string | number>({
      providesTags: ['QuickBooksRuns'],
      query: (id) => ({ method: 'get', url: `quickbooks/runs/${id}` }),
    }),
    settings: build.query<QuickBooksSettings, void>({
      providesTags: ['QuickBooksSettings'],
      query: () => ({
        method: 'get',
        url: 'quickbooks/settings',
        headers: setupRequestHeaders,
      }),
    }),
    controls: build.query<QuickBooksSettings, void>({
      providesTags: ['QuickBooksSettings'],
      query: () => ({
        method: 'get',
        url: 'quickbooks/controls',
        headers: setupRequestHeaders,
      }),
    }),
    saveSettings: build.mutation<
      QuickBooksSettings,
      Partial<QuickBooksSettings>
    >({
      invalidatesTags: ['QuickBooksSettings'],
      query: (data) => ({ data, method: 'put', url: 'quickbooks/settings' }),
    }),
    saveControls: build.mutation<
      QuickBooksSettings,
      Partial<QuickBooksSettings>
    >({
      invalidatesTags: ['QuickBooksSettings'],
      query: (data) => ({ data, method: 'put', url: 'quickbooks/controls' }),
    }),
    resolveReviewItem: build.mutation<QuickBooksReviewItem, string | number>({
      invalidatesTags: ['QuickBooksRuns'],
      query: (id) => ({
        method: 'put',
        url: `quickbooks/review-items/${id}/resolve`,
      }),
    }),
    // T-R08: the car-rental screens (same lane flag; 423 when off).
    rentalLane: build.query<QuickBooksRentalLane, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/lane',
      }),
    }),
    disconnect: build.mutation<void, QuickBooksEnvironment>({
      invalidatesTags: ['QuickBooksConnection'],
      query: (environment) => ({
        method: 'delete',
        data: { environment },
        url: 'quickbooks/connection',
      }),
    }),
    rentalAccounts: build.query<QuickBooksRentalAccounts, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/accounts',
      }),
    }),
    rentalProposals: build.query<QuickBooksRentalProposals, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/proposals',
      }),
    }),
    postDepositRun: build.mutation<
      QuickBooksDepositRun,
      QuickBooksDepositRunRequest
    >({
      invalidatesTags: ['QuickBooksRuns'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/runs/post',
      }),
    }),
    rentalSavePlan: build.query<QuickBooksRentalSavePlan, string>({
      query: (statementId) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/rentals/statements/${encodeURIComponent(statementId)}/save-plan`,
      }),
    }),
    previewDepositRun: build.mutation<
      QuickBooksDepositRun,
      QuickBooksDepositRunRequest
    >({
      invalidatesTags: ['QuickBooksRuns'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/runs/preview',
      }),
    }),
    rentalPayments: build.query<{ payments: QuickBooksRentalPayment[] }, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/payments',
      }),
    }),
    postMoneyOutRun: build.mutation<
      QuickBooksMoneyOutRun,
      QuickBooksMoneyOutRunRequest
    >({
      invalidatesTags: ['QuickBooksRuns'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/money-out/post',
      }),
    }),
    rentalCutoverReview: build.query<QuickBooksRentalCutoverReview, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/cutover-review',
      }),
    }),
    // The backend requires a body on both routes (qb-worker schemas
    // killSwitchEngageBody / killSwitchReleaseBody): engage {reason >= 3},
    // release {reason >= 10, acknowledgement === I_ACKNOWLEDGE_QBO_WRITE_RELEASE}.
    // The reason is stored on the owner's QbSettingsAudit row.
    engageKillSwitch: build.mutation<
      QuickBooksSettings,
      KillSwitchEngageRequest
    >({
      invalidatesTags: ['QuickBooksSettings'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/kill-switch/engage',
      }),
    }),
    releaseKillSwitch: build.mutation<
      QuickBooksSettings,
      KillSwitchReleaseRequest
    >({
      invalidatesTags: ['QuickBooksSettings'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/kill-switch/release',
      }),
    }),
    previewMoneyOutRun: build.mutation<
      QuickBooksMoneyOutRun,
      QuickBooksMoneyOutRunRequest
    >({
      invalidatesTags: ['QuickBooksRuns'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/money-out/preview',
      }),
    }),
    // T-R04: rental statements and the "Confirm statement" save loop (lane
    // flag qb.carRental.import; 423 when off).
    rentalStatements: build.query<QuickBooksRentalStatementList, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/statements?state=ACTIVE',
      }),
    }),
    // T-R08: the uploads waiting for a conflict answer (same date, different
    // file), listed apart so the newest-50 page of statements never hides one.
    rentalConflicts: build.query<QuickBooksRentalStatementList, void>({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/rentals/statements?state=CONFLICT',
      }),
    }),
    verifyFiservImport: build.mutation<
      QuickBooksFiservImportResult,
      QuickBooksFiservImportRequest
    >({
      invalidatesTags: ['QuickBooksRuns'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/fiserv-imports',
      }),
    }),
    uploadRentalStatement: build.mutation<
      QuickBooksRentalUploadResult,
      QuickBooksRentalUploadRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/rentals/statements',
      }),
    }),
    requestWorkerCircuitReset: build.mutation<
      QuickBooksWorkerCircuitResetRequest,
      void
    >({
      query: () => ({
        method: 'post',
        url: 'quickbooks/internal/worker-circuit/reset-request',
        data: { confirmation: 'REQUEST_QB_WORKER_CIRCUIT_RESET' },
      }),
    }),
    recordRentalPayment: build.mutation<
      { payment: QuickBooksRentalPayment },
      QuickBooksRentalPaymentRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/rentals/payments',
      }),
    }),
    controlCenterWorkerStatus: build.query<
      QuickBooksWorkerSchedulingStatus,
      void
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: () => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: 'quickbooks/control-center/worker-status',
      }),
    }),
    rentalProposalDuplicateCheck: build.query<
      QuickBooksRentalDuplicateCheck,
      string
    >({
      query: (proposalId) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/rentals/proposals/${encodeURIComponent(proposalId)}/duplicate-check`,
      }),
    }),
    createLtlShipment: build.mutation<
      QuickBooksLtlShipmentCreateResult,
      QuickBooksLtlShipmentCreateRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/control-center/ltl/shipments',
      }),
    }),
    // T-L07: the LTL screens' actions (qb-worker routes gated by the lane
    // flags, T-F04). Each writes CLIX/Qb* only; the worker books later.
    uploadRussInvoices: build.mutation<
      QuickBooksRussInvoiceUploadResult,
      { files: QuickBooksRussInvoiceUploadFile[] }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/ltl/russ-invoices',
      }),
    }),
    autoLinkProjects: build.mutation<
      QuickBooksProjectAutoLinkResult,
      { realmId: string; environment: QuickBooksEnvironment }
    >({
      invalidatesTags: ['QuickBooksProjects'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/projects/auto-link',
      }),
    }),
    createLtlBatchDraft: build.mutation<
      QuickBooksControlCenterDryRunResult,
      QuickBooksLtlBatchActionRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/control-center/ltl/batches/draft',
      }),
    }),
    connection: build.query<QuickBooksConnection | null, QuickBooksEnvironment>(
      {
        providesTags: ['QuickBooksConnection'],
        query: (environment) => ({
          method: 'get',
          headers: setupRequestHeaders,
          url: `quickbooks/connection?environment=${environment}`,
        }),
      }
    ),
    refreshProjects: build.mutation<
      { projects: unknown[]; reviewRequired: boolean },
      { realmId: string; environment: QuickBooksEnvironment }
    >({
      invalidatesTags: ['QuickBooksProjects'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/projects/refresh',
      }),
    }),
    createCarRentalBatchDraft: build.mutation<
      QuickBooksControlCenterDryRunResult,
      QuickBooksCarRentalActionRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/control-center/car-rentals/batches/draft',
      }),
    }),
    // "What was it?" (owner only; never writes to QuickBooks).
    recordPayoutResidualLink: build.mutation<
      { link: QuickBooksPayoutResidualLink },
      QuickBooksPayoutResidualLinkRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/control-center/payout-residual-links',
      }),
    }),
    controlCenterEventDryRun: build.mutation<
      QuickBooksControlCenterDryRunResult,
      { eventId: number | string; data: QuickBooksControlCenterDryRunRequest }
    >({
      query: ({ data, eventId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/events/${eventId}/reconcile/dry-run`,
      }),
    }),
    rentalStatementDetail: build.query<QuickBooksRentalStatementDetail, string>(
      {
        providesTags: ['QuickBooksControlCenter'],
        query: (statementId) => ({
          method: 'get',
          headers: setupRequestHeaders,
          url: `quickbooks/rentals/statements/${encodeURIComponent(statementId)}`,
        }),
      }
    ),
    undoPayoutResidualLink: build.mutation<
      { link: QuickBooksPayoutResidualLink },
      number
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (id) => ({
        method: 'post',
        url: `quickbooks/control-center/payout-residual-links/${encodeURIComponent(
          String(id)
        )}/undo`,
      }),
    }),
    decideRentalCutover: build.mutation<
      { decisions: unknown[] },
      QuickBooksRentalDecideRequest
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ lineId, ...data }) => ({
        data,
        method: 'post',
        url: `quickbooks/rentals/cutover-review/${encodeURIComponent(lineId)}/decide`,
      }),
    }),
    draftRussPayment: build.mutation<
      { persisted: boolean; batchId: string | null },
      QuickBooksLtlTenantBody & QuickBooksRussPaymentFacts
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/control-center/ltl/batches/draft',
      }),
    }),
    beginConnect: build.mutation<
      {
        expiresAt: string;
        authorizationUrl: string;
        authorizationStatus: string;
      },
      { returnPath: string; environment: QuickBooksEnvironment }
    >({
      invalidatesTags: ['QuickBooksConnection'],
      query: (data) => ({ data, method: 'post', url: 'quickbooks/connect' }),
    }),
    accounts: build.query<
      { accounts: QuickBooksAccount[] },
      { realmId: string; environment: QuickBooksEnvironment }
    >({
      query: ({ realmId, environment }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/accounts?environment=${environment}&realmId=${encodeURIComponent(realmId)}`,
      }),
    }),
    patchLtlBatch: build.mutation<
      QuickBooksControlCenterDryRunResult,
      { batchId: string; data: QuickBooksLtlBatchEditRequest }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, batchId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/batches/${batchId}/edit`,
      }),
    }),
    editLtlShipment: build.mutation<
      QuickBooksLtlShipmentEditResult,
      { shipmentId: string; data: QuickBooksLtlShipmentEditRequest }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, shipmentId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/${shipmentId}/edit`,
      }),
    }),
    claimLtlBatch: build.mutation<
      QuickBooksControlCenterDryRunResult,
      { batchId: string; data: QuickBooksLtlBatchActionRequest }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, batchId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/batches/${batchId}/claim`,
      }),
    }),
    editLtlInvoice: build.mutation<
      QuickBooksLtlInvoiceWriteResult,
      { invoiceId: string; data: QuickBooksLtlInvoiceEditRequest }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, invoiceId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/invoices/${invoiceId}/edit`,
      }),
    }),
    createLtlInvoice: build.mutation<
      QuickBooksLtlInvoiceWriteResult,
      { shipmentId: string; data: QuickBooksLtlInvoiceCreateRequest }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, shipmentId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/${shipmentId}/invoices`,
      }),
    }),
    controlCenterItemDryRun: build.mutation<
      QuickBooksControlCenterDryRunResult,
      { itemKey: string; data: QuickBooksControlCenterDryRunRequest }
    >({
      query: ({ data, itemKey }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/items/${encodeURIComponent(
          itemKey
        )}/reconcile/dry-run`,
      }),
    }),
    confirmRentalProposalSave: build.mutation<
      { state: string; expenseId: number; alreadySaved: boolean },
      { expenseId: number; proposalId: string }
    >({
      query: ({ expenseId, proposalId }) => ({
        method: 'post',
        data: { expenseId },
        url: `quickbooks/rentals/proposals/${encodeURIComponent(proposalId)}/confirm-save`,
      }),
    }),
    markRentalToll: build.mutation<
      unknown,
      { proposalId: string; letterReceiptUrl: string }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ proposalId, letterReceiptUrl }) => ({
        method: 'post',
        data: { letterReceiptUrl },
        url: `quickbooks/rentals/proposals/${encodeURIComponent(proposalId)}/mark-toll`,
      }),
    }),
    decideLtlRussExpense: build.mutation<
      { decision: string },
      { expenseId: number; decision: 'HANDLED_BY_HAND' | 'NOT_RUSS_FREIGHT' }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ decision, expenseId }) => ({
        method: 'post',
        data: { decision },
        url: `quickbooks/ltl/russ-expenses/${expenseId}/decision`,
      }),
    }),
    // Overcharges kept: "Customer asked for a refund" (Company Owner only;
    // the refund itself is done in Clover).
    markOverchargeRefundRequested: build.mutation<
      { key: string; created: boolean; refundRequestedAt: string },
      { key: string }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ key }) => ({
        method: 'post',
        url: `quickbooks/control-center/overcharges-kept/${encodeURIComponent(
          key
        )}/refund-requested`,
      }),
    }),
    setAutonomousDeterministicWrites: build.mutation<
      QuickBooksSettings,
      {
        realmId: string;
        enabled: boolean;
        environment: QuickBooksEnvironment;
      }
    >({
      invalidatesTags: ['QuickBooksSettings'],
      query: (data) => ({
        data,
        method: 'put',
        url: 'quickbooks/controls/autonomous-deterministic-writes',
      }),
    }),
    mappings: build.query<
      QuickBooksMapping[],
      { realmId: string; environment: QuickBooksEnvironment }
    >({
      providesTags: ['QuickBooksMappings'],
      query: ({ realmId, environment }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/mappings?environment=${environment}&realmId=${encodeURIComponent(realmId)}`,
      }),
    }),
    saveMappings: build.mutation<
      QuickBooksMapping[],
      {
        realmId: string;
        entries: QuickBooksMapping[];
        environment: QuickBooksEnvironment;
        productionCreateApproval?: 'approved_production_setup_accounts';
      }
    >({
      invalidatesTags: ['QuickBooksMappings'],
      query: (data) => ({ data, method: 'put', url: 'quickbooks/mappings' }),
    }),
    projects: build.query<
      { links: QuickBooksProjectLink[] },
      { realmId: string; environment: QuickBooksEnvironment }
    >({
      providesTags: ['QuickBooksProjects'],
      query: ({ realmId, environment }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/projects?environment=${environment}&realmId=${encodeURIComponent(realmId)}`,
      }),
    }),
    // T-L05: LTL freight per Russ invoice (lane flag qb.ltl.read; 423 off).
    ltlFreight: build.query<QuickBooksLtlFreight, { eventId?: number } | void>({
      providesTags: ['QuickBooksControlCenter'],
      query: (query) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: query?.eventId
          ? `quickbooks/ltl/freight?eventId=${encodeURIComponent(
              String(query.eventId)
            )}`
          : 'quickbooks/ltl/freight',
      }),
    }),
    resolveRentalStatementConflict: build.mutation<
      { statement?: QuickBooksRentalStatement },
      { statementId: string; resolution: 'SUPERSEDE' | 'KEEP_EARLIER' }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ resolution, statementId }) => ({
        method: 'post',
        data: { resolution },
        url: `quickbooks/rentals/statements/${encodeURIComponent(statementId)}/conflict`,
      }),
    }),
    rentalPaymentAction: build.mutation<
      { payment: QuickBooksRentalPayment },
      {
        id: string;
        data: Record<string, unknown>;
        action: 'cancel' | 'bank-row' | 'takeover';
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ id, data, action }) => ({
        data,
        method: 'post',
        url: `quickbooks/rentals/payments/${encodeURIComponent(id)}/${action}`,
      }),
    }),
    editRussPayment: build.mutation<
      { batchId: string; version: number },
      {
        batchId: string;
        data: QuickBooksLtlTenantBody &
          QuickBooksRussPaymentFacts & { expectedVersion: number };
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, batchId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/batches/${encodeURIComponent(batchId)}/edit`,
      }),
    }),
    settleLtlBatch: build.mutation<
      QuickBooksControlCenterDryRunResult,
      {
        batchId: string;
        idempotencyKey: string;
        data: QuickBooksBatchSettleRequest;
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, batchId, idempotencyKey }) => ({
        data,
        method: 'post',
        headers: { 'Idempotency-Key': idempotencyKey },
        url: `quickbooks/control-center/ltl/batches/${batchId}/settle`,
      }),
    }),
    controlCenterMoneyOutCandidates: build.query<
      QuickBooksMoneyOutCandidatesResult,
      QuickBooksMoneyOutCandidatesQuery
    >({
      query: (params) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/research/money-out/assignment-candidates?${new URLSearchParams(
          Object.entries(params).filter(([, value]) => value !== undefined) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    settleCarRentalBatch: build.mutation<
      QuickBooksControlCenterDryRunResult,
      {
        batchId: string;
        idempotencyKey: string;
        data: QuickBooksBatchSettleRequest;
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, batchId, idempotencyKey }) => ({
        data,
        method: 'post',
        headers: { 'Idempotency-Key': idempotencyKey },
        url: `quickbooks/control-center/car-rentals/batches/${batchId}/settle`,
      }),
    }),
    // Control Center redesign, step 2a. Scope only (realmId, environment):
    // the server's querystring schema allows nothing else. The response is
    // the complete current decision set, not a page. A cold read rebuilds
    // the Overview projection (~20 s on Dev); the server caches it per
    // revision, and the client keeps the last answer for 15 minutes so a
    // revisit or a failed refresh still shows it.
    controlCenterNeedsAttention: build.query<
      QuickBooksNeedsAttention,
      Pick<
        QuickBooksControlCenterQuery,
        'realmId' | 'environment' | 'cacheCompanyId'
      > | void
    >({
      keepUnusedDataFor: 15 * 60,
      providesTags: ['QuickBooksControlCenter'],
      query: (query) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/needs-attention?${controlCenterNeedsAttentionSearch(
          query
        )}`,
      }),
    }),
    // J8, 2026-08-19 -- mirrors controlCenterCarRentalBatches, which has
    // always existed. Backend route added in the same chunk
    // (clix-backend@cb5288f) since no LTL batch list existed before this.
    controlCenterLtlBatches: build.query<
      QuickBooksControlCenterList,
      QuickBooksControlCenterQuery | void
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: (query = {}) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/ltl/batches?${new URLSearchParams(
          Object.entries(query || {}).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterCarRentalImports: build.query<
      QuickBooksControlCenterList,
      QuickBooksControlCenterQuery | void
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: (query = {}) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/car-rentals/imports?${new URLSearchParams(
          Object.entries(query || {}).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterCarRentalBatches: build.query<
      QuickBooksControlCenterList,
      QuickBooksControlCenterQuery | void
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: (query = {}) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/car-rentals/batches?${new URLSearchParams(
          Object.entries(query || {}).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterOverview: build.query<
      QuickBooksControlCenterOverview,
      QuickBooksControlCenterQuery | void
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: (query = {}) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/overview?${new URLSearchParams(
          Object.entries(query || {}).filter(
            ([key, value]) => key !== 'cacheCompanyId' && Boolean(value)
          ) as [string, string][]
        ).toString()}`,
      }),
    }),
    dryRunSync: build.mutation<
      {
        mode: 'dry-run';
        canPost: boolean;
        wouldPost: string;
        postedMaps: number;
        openReviewItems: number;
        controls: Pick<
          QuickBooksSettings,
          'enabled' | 'killSwitch' | 'scheduleJson' | 'closedThroughDate'
        >;
      },
      { realmId?: string; environment?: QuickBooksEnvironment } | void
    >({
      query: (data) => ({
        method: 'post',
        data: data || {},
        url: 'quickbooks/sync/dry-run',
      }),
    }),
    // T-L02 route (qb-worker quickbooksRussInvoiceRegister.js
    // markRussInvoiceSplitIntended): the owner confirms that one Russ
    // invoice # on several stock records is a split load. Writes only the
    // Qb* mark; the Needs attention item closes on the next read.
    markLtlRussSplitIntended: build.mutation<
      {
        qboTouched: false;
        sourceKeys: string[];
        invoiceNumber: string;
        alreadyMarked: boolean;
      },
      { invoiceNumber: string }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ invoiceNumber }) => ({
        // An empty JSON object: the worker refuses an empty JSON body.
        data: {},
        method: 'post',
        url: `quickbooks/ltl/russ-register/${encodeURIComponent(invoiceNumber)}/split-intended`,
      }),
    }),
    reviewItems: build.query<
      { count: number; rows: QuickBooksReviewItem[] },
      { status?: 'open' | 'resolved'; environment?: QuickBooksEnvironment }
    >({
      providesTags: ['QuickBooksRuns'],
      query: ({ status, environment } = {}) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/review-items?${new URLSearchParams(
          Object.entries({ status, environment }).filter(([, value]) =>
            Boolean(value)
          ) as [string, string][]
        ).toString()}`,
      }),
    }),
    controlCenterLtlBatch: build.query<
      QuickBooksControlCenterLtlBatchDetail,
      { batchId: string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ batchId, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/ltl/batches/${batchId}?${new URLSearchParams(
          Object.entries(query).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterReviewDecision: build.mutation<
      QuickBooksControlCenterDryRunResult,
      {
        itemKey: string;
        idempotencyKey: string;
        data: QuickBooksControlCenterReviewDecisionRequest;
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, itemKey, idempotencyKey }) => ({
        data,
        method: 'post',
        headers: { 'idempotency-key': idempotencyKey },
        url: `quickbooks/control-center/items/${encodeURIComponent(
          itemKey
        )}/review-decision`,
      }),
    }),
    controlCenterLtlShipment: build.query<
      QuickBooksControlCenterLtlShipmentDetail,
      { shipmentId: string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ shipmentId, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/ltl/${shipmentId}?${new URLSearchParams(
          Object.entries(query).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterLtlInvoice: build.query<
      QuickBooksControlCenterLtlInvoiceDetail,
      { invoiceId: string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ invoiceId, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/ltl/invoices/${invoiceId}?${new URLSearchParams(
          Object.entries(query).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterCarRentalBatch: build.query<
      QuickBooksControlCenterRentalBatchDetail,
      { batchId: string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ batchId, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/car-rentals/batches/${batchId}?${new URLSearchParams(
          Object.entries(query).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][]
        ).toString()}`,
      }),
    }),
    controlCenterAuthorizeEffect: build.mutation<
      QuickBooksControlCenterEffectAuthorizationResult,
      {
        itemKey: string;
        idempotencyKey: string;
        data: QuickBooksControlCenterEffectAuthorizationRequest;
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, itemKey, idempotencyKey }) => ({
        data,
        method: 'post',
        headers: { 'idempotency-key': idempotencyKey },
        url: `quickbooks/control-center/items/${encodeURIComponent(
          itemKey
        )}/authorize-effect`,
      }),
    }),
    controlCenterEventExport: build.query<
      QuickBooksControlCenterEventExport,
      { eventId: number | string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ eventId, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/events/${eventId}/export?${new URLSearchParams(
          Object.entries(query).filter(
            ([key, value]) => key !== 'cacheCompanyId' && Boolean(value)
          ) as [string, string][]
        ).toString()}`,
      }),
    }),
    controlCenterEvidence: build.query<
      QuickBooksControlCenterEvidence,
      { itemKey: string } & QuickBooksControlCenterQuery
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ itemKey, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/items/${encodeURIComponent(
          itemKey
        )}/evidence?${new URLSearchParams(
          Object.entries(query).filter(
            ([key, value]) => key !== 'cacheCompanyId' && Boolean(value)
          ) as [string, string][]
        ).toString()}`,
      }),
    }),
    // The worker never writes a link from this route: it records the
    // proposal as a review (qb-worker quickbooks.js pairProject) and the
    // project-link reconciler links the event itself once QuickBooks has
    // exactly one project with the expected name.
    pairProject: build.mutation<
      {
        status: string;
        linked: boolean;
        reason?: string;
        reviewItemId?: number | string;
      },
      {
        eventId: number;
        realmId: string;
        qboProjectId: string;
        qboProjectName: string;
        state?: 'blocked' | 'verified';
        environment: QuickBooksEnvironment;
        matchMethod?: 'manual' | 'assisted';
      }
    >({
      invalidatesTags: ['QuickBooksProjects'],
      query: ({ eventId, ...data }) => ({
        data,
        method: 'put',
        url: `quickbooks/projects/${eventId}/pair`,
      }),
    }),
    createMissingAccounts: build.mutation<
      {
        accounts: QuickBooksAccount[];
        created: {
          sourceKey: string;
          qboAccountId: string;
          qboAccountName: string;
        }[];
        skipped: {
          reason: string;
          sourceKey: string;
          qboAccountId?: string;
          qboAccountName?: string;
        }[];
      },
      {
        realmId: string;
        entries: QuickBooksMapping[];
        environment: QuickBooksEnvironment;
      }
    >({
      query: (data) => ({
        data,
        method: 'post',
        url: 'quickbooks/accounts/create-missing',
      }),
    }),
    // Owner feedback + instructor ruling, 2026-08-20 -- drawer-only,
    // read-only, by-id QBO facts lookup (see the backend route's own
    // comment for the read-only/Deposit-Purchase-only/never-a-table
    // reasoning). Only called from evidence-drawer.tsx, after the base
    // evidence() response resolves with a qboId -- never from a table.
    controlCenterEvidenceQboFacts: build.query<
      QuickBooksEvidenceQboFacts | null,
      QuickBooksControlCenterQuery & {
        itemKey: string;
        entityType: string;
        qboEntityId: string;
      }
    >({
      providesTags: ['QuickBooksControlCenter'],
      query: ({ itemKey, ...query }) => ({
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/items/${encodeURIComponent(
          itemKey
        )}/evidence/qbo-facts?${new URLSearchParams(
          Object.entries(query).filter(
            ([key, value]) => key !== 'cacheCompanyId' && Boolean(value)
          ) as [string, string][]
        ).toString()}`,
      }),
    }),
    russPaymentAction: build.mutation<
      { batchId: string },
      {
        batchId: string;
        action:
          | 'approve'
          | 'bank-row'
          | 'takeover'
          | 'rebook-yes'
          | 'accountant-move-yes';
        data: QuickBooksLtlTenantBody & {
          lineId?: string;
          moveCents?: number;
          purchaseId?: string;
          qboEntityId?: string;
          expectedVersion?: number;
        };
      }
    >({
      invalidatesTags: ['QuickBooksControlCenter'],
      query: ({ data, action, batchId }) => ({
        data,
        method: 'post',
        url: `quickbooks/control-center/ltl/batches/${encodeURIComponent(batchId)}/${action}`,
      }),
    }),
    controlCenterEventWorkspace: build.query<
      QuickBooksControlCenterEventWorkspace,
      { eventId: number | string } & QuickBooksControlCenterQuery
    >({
      // Event workspaces are expensive, evidence-heavy projections. Keep the
      // last successful, event-scoped response available across drawer/tab
      // navigation so the component can render it while a revisit revalidates
      // in the background. RTK Query's serialized argument (eventId + realm +
      // environment) remains the cache boundary, so one Event can never seed
      // another Event's workspace.
      keepUnusedDataFor: 15 * 60,
      providesTags: ['QuickBooksControlCenter'],
      queryFn: async (
        { eventId, ...query },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          method: 'get',
          headers: setupRequestHeaders,
          url: `quickbooks/control-center/events/${eventId}?${controlCenterEventWorkspaceSearch(
            query
          )}`,
        });
        if (result.error) {
          return { error: result.error };
        }
        const workspace = result.data as QuickBooksControlCenterEventWorkspace;
        const unavailableError =
          controlCenterEventWorkspaceUnavailableError(workspace);
        // The route intentionally returns its fail-closed current-state
        // contract with HTTP 200. Treat that transient contract as a rejected
        // refresh so RTK Query retains any previously verified response for
        // this exact serialized event/company/realm/environment key instead of
        // replacing it with an unusable empty projection.
        return unavailableError
          ? { error: unavailableError }
          : { data: workspace };
      },
    }),
    controlCenterList: build.query<
      QuickBooksControlCenterList,
      QuickBooksControlCenterQuery & {
        // Client-only: read every page of the list (each at `limit`) and
        // return them as one list, so a lane never silently shows only the
        // first page (Deposits on Dev: 398 rows, the server's largest page
        // is 200 -- qb-worker quickbooksControlCenter.js MAX_LIMIT).
        allPages?: boolean;
        surface:
          | 'ltl'
          | 'events'
          | 'payouts'
          | 'history'
          | 'deposits'
          | 'expenses'
          | 'car-rentals'
          | 'review-items';
      }
    >({
      keepUnusedDataFor: 15 * 60,
      providesTags: ['QuickBooksControlCenter'],
      onQueryStarted: (arg, api) =>
        readRestInBackground(
          arg,
          api,
          readControlCenterPage(arg),
          controlCenterListSetter
        ),
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const { surface, allPages, ...query } = arg;
        const read = (offset: number) =>
          baseQuery({
            method: 'get',
            headers: setupRequestHeaders,
            url: `quickbooks/control-center/${surface}?${new URLSearchParams(
              Object.entries({
                ...query,
                ...withinServerLimit(query.limit),
                offset,
              }).filter(
                ([key, value]) => key !== 'cacheCompanyId' && Boolean(value)
              ) as [string, string][]
            ).toString()}`,
          });
        const first = await read(query.offset ?? 0);
        if (first.error) {
          return { error: first.error };
        }
        const list = first.data as QuickBooksControlCenterList;
        // Page one paints at once; the rest is read in the background. A
        // list already held whole stays on screen until the new one is.
        const previous = allPages
          ? cachedControlCenterList(arg, api.getState())
          : undefined;
        return {
          data: allPages ? firstPageWhileReading(list, previous) : list,
        };
      },
    }),
  }),
});

// A page is never asked larger than the worker answers (MAX_LIMIT 200).
function withinServerLimit(limit?: number) {
  return limit == null
    ? {}
    : { limit: Math.min(Number(limit), CONTROL_CENTER_SERVER_MAX_LIMIT) };
}

// One page of a Control Center list, as controlCenterList's queryFn reads
// it (the background read of an `allPages` list).
function readControlCenterPage({
  surface,
  ...query
}: QuickBooksControlCenterQuery & { surface: string; allPages?: boolean }) {
  return (offset: number) =>
    quickBooksApiQuery(
      {
        method: 'get',
        headers: setupRequestHeaders,
        url: `quickbooks/control-center/${surface}?${new URLSearchParams(
          Object.entries({
            ...query,
            ...withinServerLimit(query.limit),
            offset,
          })
            .filter(
              ([key, value]) =>
                key !== 'cacheCompanyId' && key !== 'allPages' && Boolean(value)
            )
            .map(([key, value]) => [key, String(value)])
        ).toString()}`,
      },
      { signal: new AbortController().signal } as Parameters<
        typeof quickBooksApiQuery
      >[1],
      {}
    );
}

// Where readRestInBackground puts a list once every page is read. Typed
// without quickBooksApi so the API's own type does not depend on itself.
function controlCenterListSetter(
  arg: unknown,
  recipe: (
    cached: QuickBooksControlCenterList
  ) => QuickBooksControlCenterList | undefined
): unknown {
  return quickBooksApi.util.updateQueryData(
    'controlCenterList',
    arg as never,
    recipe as never
  );
}

export const {
  useMappingsQuery,
  useProjectsQuery,
  useSettingsQuery,
  useControlsQuery,
  useConnectionQuery,
  useRunDetailsQuery,
  useLtlFreightQuery,
  // T-R08
  useRentalLaneQuery,
  useReviewItemsQuery,
  useLazyAccountsQuery,
  useDryRunSyncMutation,
  useDisconnectMutation,
  useRentalSavePlanQuery,
  usePairProjectMutation,
  useRentalAccountsQuery,
  useRentalPaymentsQuery,
  useRentalConflictsQuery,
  useBeginConnectMutation,
  useSaveControlsMutation,
  useSaveMappingsMutation,
  useSaveSettingsMutation,
  useRentalProposalsQuery,
  useRescanRentalsMutation,
  useRentalStatementsQuery,
  usePatchLtlBatchMutation,
  useClaimLtlBatchMutation,
  useMarkRentalTollMutation,
  useControlCenterListQuery,
  useSettleLtlBatchMutation,
  usePostDepositRunMutation,
  useEditLtlInvoiceMutation,
  useEditRussPaymentMutation,
  usePostMoneyOutRunMutation,
  useRefreshProjectsMutation,
  useEditLtlShipmentMutation,
  useRentalCutoverReviewQuery,
  useDraftRussPaymentMutation,
  useAutoLinkProjectsMutation,
  useEngageKillSwitchMutation,
  useCreateLtlInvoiceMutation,
  useRussPaymentActionMutation,
  useReleaseKillSwitchMutation,
  useResolveReviewItemMutation,
  usePreviewDepositRunMutation,
  useCreateLtlShipmentMutation,
  useRentalStatementDetailQuery,
  useUploadRussInvoicesMutation,
  useControlCenterOverviewQuery,
  useControlCenterEvidenceQuery,
  useControlCenterLtlBatchQuery,
  useVerifyFiservImportMutation,
  usePreviewMoneyOutRunMutation,
  useRecordRentalPaymentMutation,
  useRentalPaymentActionMutation,
  useDecideRentalCutoverMutation,
  useCreateLtlBatchDraftMutation,
  useDecideLtlRussExpenseMutation,
  useControlCenterLtlBatchesQuery,
  useSettleCarRentalBatchMutation,
  useControlCenterLtlInvoiceQuery,
  useUploadRentalStatementMutation,
  useControlCenterLtlShipmentQuery,
  useControlCenterEventExportQuery,
  useCreateMissingAccountsMutation,
  useControlCenterWorkerStatusQuery,
  useUndoPayoutResidualLinkMutation,
  useControlCenterItemDryRunMutation,
  useMarkLtlRussSplitIntendedMutation,
  useControlCenterNeedsAttentionQuery,
  useControlCenterCarRentalBatchQuery,
  useControlCenterEventDryRunMutation,
  useControlCenterEventWorkspaceQuery,
  useRecordPayoutResidualLinkMutation,
  useRequestWorkerCircuitResetMutation,
  useLazyControlCenterEventExportQuery,
  useCreateCarRentalBatchDraftMutation,
  useControlCenterEvidenceQboFactsQuery,
  useControlCenterCarRentalImportsQuery,
  useControlCenterCarRentalBatchesQuery,
  useControlCenterReviewDecisionMutation,
  useControlCenterAuthorizeEffectMutation,
  useMarkOverchargeRefundRequestedMutation,
  useResolveRentalStatementConflictMutation,
  useSetAutonomousDeterministicWritesMutation,
  useLazyControlCenterMoneyOutCandidatesQuery,
} = quickBooksApi;

selectCachedControlCenterList = (arg, state) =>
  quickBooksApi.endpoints.controlCenterList.select(
    arg as Parameters<
      typeof quickBooksApi.endpoints.controlCenterList.select
    >[0]
  )(state as never)?.data;
